import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, BookOpen, Mic, MessageCircle, Volume2, ChevronRight, ChevronLeft,
  CheckCircle, XCircle, Award, Flame, Heart, Trophy, LogIn, UserPlus, LogOut,
  Lock, Unlock, Sparkles, Plus, Trash2, Save, Loader2, Settings, Users, BarChart3, Play, Pause,
  Star, Gauge, Eye, ShieldCheck, Globe, ExternalLink, HelpCircle, Phone, MapPin, Download, X, Bell
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import WelcomeScreen      from '../components/academy/WelcomeScreen';
import Onboarding         from '../components/academy/Onboarding';
import Dashboard          from '../components/academy/Dashboard';
import QuickSession       from '../components/academy/QuickSession';
import ScenariosPage      from '../components/academy/Scenarios';
import ListeningPlaylist  from '../components/academy/ListeningPlaylist';
import Readings           from '../components/academy/Readings';
import ReviewPage         from '../components/academy/Review';
import CertificatesPage   from '../components/academy/Certificates';
import LevelTest          from '../components/academy/LevelTest';
import FormationIntro     from '../components/academy/FormationIntro';
import AnimatedBrandBackground from '../components/academy/AnimatedBrandBackground';
import LessonCompleteScreen from '../components/academy/LessonCompleteScreen';
import { SOCIALS } from '../components/academy/brand';
import GettingStartedGuide, { shouldShowGuide, markGuideSeen } from '../components/academy/GettingStartedGuide';
import XpGainToast        from '../components/academy/XpGainToast';
import AcademyTutorWordModal from '../components/academy/AcademyTutorWordModal';
import AcademyTutorChat from '../components/academy/AcademyTutorChat';
import PaywallScreen      from '../components/academy/PaywallScreen';
import AdminPricing       from '../components/academy/AdminPricing';
import AdminPaymentSettings from '../components/academy/AdminPaymentSettings';
import AdminPayments      from '../components/academy/AdminPayments';
import AdminRevenue       from '../components/academy/AdminRevenue';
import AdminCountryPricing from '../components/academy/AdminCountryPricing';
import AdminGrants        from '../components/academy/AdminGrants';
import AdminDrafts        from '../components/academy/AdminDrafts';
import AdminCountryStats  from '../components/academy/AdminCountryStats';
import FormationsListPage from '../components/academy/FormationsListPage';
import BlogPage           from '../components/academy/BlogPage';
import ProFormationsPage  from '../components/academy/ProFormationsPage';
import WebAiFormationPage from '../components/academy/WebAiFormationPage';
import Conjugation        from '../components/academy/Conjugation';
import Audiobooks         from '../components/academy/Audiobooks';
import StudentMessages    from '../components/academy/StudentMessages';
import AdminMessages      from '../components/academy/AdminMessages';
import { getT } from '../components/academy/i18n';
import ShareProgressButton from '../components/academy/ShareProgressButton';
import { apiCall as apiX, speak, stopSpeaking, track } from '../components/academy/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/academy`;

// Libellés lisibles des vues pour les statistiques admin (modules les plus cliqués)
const VIEW_LABELS = {
  home: 'Accueil', dashboard: 'Tableau de bord', 'formation-intro': 'Cours d\'anglais',
  'formations-list': 'Nos formations', 'pro-formations': 'Formations Pro',
  'webai-formation': 'Formation Sites web IA', blog: 'Blog', 'level-test': 'Petit test',
  catalog: 'Catalogue de leçons', lesson: 'Leçon', quick: 'Session rapide',
  scenarios: 'Scénarios', listening: 'Écoute', readings: 'Bibliothèque',
  audiobooks: 'Audiobooks', review: 'Révision', certificates: 'Certificats',
  favorites: 'Favoris', profile: 'Profil', about: 'À propos', faq: 'FAQ',
  contact: 'Contact', terms: 'Conditions', login: 'Connexion', paywall: 'Paiement',
  welcome: 'Bienvenue', onboarding: 'Onboarding',
};

// === Helpers ===
const api = async (path, method = 'GET', body = null, token = null) => {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}${path}`, opts);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur');
  }
  return r.json();
};

// === Main page ===
export default function AcademyPage() {
  const [token, setToken] = useState(() => localStorage.getItem('academyToken') || '');
  const [user, setUser] = useState(null);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('academyAdminToken') || '');
  const [view, setView] = useState('home');
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [paywallModule, setPaywallModule] = useState(null);
  const [paywallPricing, setPaywallPricing] = useState(null);
  const [siteLang, setSiteLang] = useState(() => localStorage.getItem('academyLang') || 'fr');
  const [siteSettings, setSiteSettings] = useState({});
  const [showGuide, setShowGuide] = useState(false);
  const [levelStatus, setLevelStatus] = useState({});
  const [openAccess, setOpenAccess] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  // Pastille "messages non lus" pour l'élève (hors admin). Rafraîchie au changement de vue.
  const refreshUnread = useCallback(() => {
    if (!token || user?.is_admin) { setUnreadMsgs(0); return; }
    api('/messages/unread-count', 'GET', null, token)
      .then(d => setUnreadMsgs(d.unread || 0)).catch(() => {});
  }, [token, user?.is_admin]);
  useEffect(() => { refreshUnread(); }, [refreshUnread, view]);

  // Mode "toutes les leçons ouvertes" (test/lancement), contrôlé depuis le panel admin
  useEffect(() => {
    api('/config').then(c => setOpenAccess(!!c.open_access)).catch(() => {});
  }, [view]);

  // Charge l'état de complétion par niveau (verrouillage inter-niveaux)
  const loadLevelStatus = useCallback(async () => {
    if (!token) { setLevelStatus({}); return; }
    try {
      const rows = await api('/progression/levels', 'GET', null, token);
      const map = {}; rows.forEach(r => { map[r.module_id] = r; });
      setLevelStatus(map);
    } catch (e) { /* ignore */ }
  }, [token]);
  useEffect(() => { loadLevelStatus(); }, [loadLevelStatus, view]);

  // Un niveau est déverrouillé si c'est le 1er, ou si le niveau précédent est terminé à 100%
  const isLevelUnlocked = useCallback((mod) => {
    if (openAccess) return true;
    const o = (mod && mod.order) || 0;
    if (o <= 1) return true;
    const prev = modules.find(m => (m.order || 0) === o - 1);
    if (!prev) return true;
    return !!levelStatus[prev.id]?.is_complete;
  }, [modules, levelStatus, openAccess]);

  // Dans le cours d'anglais, l'interface est forcée en anglais (le reste du site reste bilingue)
  const ENGLISH_COURSE_VIEWS = ['formation-intro', 'dashboard', 'catalog', 'lesson', 'quick', 'scenarios', 'listening', 'readings', 'review', 'certificates', 'favorites', 'level-test', 'profile', 'audiobooks'];
  const uiLang = ((view === 'home' && user) || ENGLISH_COURSE_VIEWS.includes(view)) ? 'en' : siteLang;

  // Persist language choice
  useEffect(() => { localStorage.setItem('academyLang', siteLang); }, [siteLang]);

  // Auto-open the guide on first visit
  useEffect(() => {
    if (shouldShowGuide()) {
      const t = setTimeout(() => setShowGuide(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  // Load site settings (social links etc) once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then(r => r.ok ? r.json() : {})
      .then(setSiteSettings)
      .catch(() => {});
  }, []);

  // Check Stripe redirect on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('stripe_session');
    if (sid && token) {
      api(`/payments/stripe/status/${sid}`, 'GET', null, token)
        .then(r => {
          if (r.payment_status === 'paid') toast.success('🎉 Paiement réussi ! Module débloqué.');
          else toast.info('Paiement en cours de traitement...');
          // Clean URL
          window.history.replaceState({}, '', '/academy');
        })
        .catch(() => {});
    }
  }, [token]);

  useEffect(() => { document.title = 'JoSmartTech Academy — Apprenez. Pratiquez. Maîtrisez.'; }, []);

  // Analytics : 1 visite enregistrée par session (visiteurs avec ou sans compte)
  useEffect(() => {
    if (!sessionStorage.getItem('academyVisitSent')) {
      sessionStorage.setItem('academyVisitSent', '1');
      track('visit', null, null, !!token);
    }
  }, []);

  // Analytics : enregistre chaque module/vue consulté (sauf le panel admin)
  useEffect(() => {
    if (view === 'admin') return;
    track('view', view, VIEW_LABELS[view] || view, !!token);
  }, [view]);

  const loadModules = useCallback(async () => {
    try { setModules(await api('/modules')); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  useEffect(() => {
    if (token) {
      api('/auth/me', 'GET', null, token).then(async u => {
        setUser(u);
        // Sync de la progression faite en tant qu'invité (avant création de compte)
        await syncGuestProgress(token);
        loadLevelStatus();
        // Streak daily ping (best-effort)
        apiX('/streak/ping', 'POST', null, token).catch(() => {});
        // Auto-route based on onboarding status
        setView(prev => {
          if (prev === 'login') {
            return u.onboarding_done ? 'dashboard' : 'onboarding';
          }
          return prev;
        });
      }).catch(() => { setToken(''); localStorage.removeItem('academyToken'); });
    }
  }, [token]);

  const persistToken = (t) => { setToken(t); if (t) localStorage.setItem('academyToken', t); else localStorage.removeItem('academyToken'); };
  const persistAdmin = (t) => { setAdminToken(t); if (t) localStorage.setItem('academyAdminToken', t); else localStorage.removeItem('academyAdminToken'); };

  // Politique d'accès par niveau : 1 = public, 2 = gratuit avec compte, 3+ = payant
  const tierOf = (mod) => {
    if (openAccess) return 'public';
    const o = (mod && mod.order) || 0;
    if (o <= 1) return 'public';
    if (o === 2) return 'account';
    return 'premium';
  };

  const openModule = async (mod) => {
    setActiveModule(mod);
    const tier = tierOf(mod);
    if (tier !== 'public' && !user) {
      toast.info(tier === 'account'
        ? 'Créez un compte gratuit pour accéder au Niveau 2.'
        : 'Connectez-vous pour débloquer ce niveau.');
      setView('login');
      return;
    }
    if (user && !isLevelUnlocked(mod)) {
      toast.info('Termine d\'abord le niveau précédent à 100% pour débloquer celui-ci.');
      return;
    }
    if (tier === 'premium' && token) {
      try {
        const access = await apiX('/my-access', 'GET', null, token);
        if (access[mod.id] === false) {
          const pricings = await apiX('/pricing');
          const p = pricings.find(x => x.module_id === mod.id) || { module_id: mod.id };
          setPaywallModule(mod);
          setPaywallPricing(p);
          setView('paywall');
          return;
        }
      } catch (e) { /* fail-open: don't block on errors */ }
    }
    const lst = await api(`/modules/${mod.id}/lessons`);
    setLessons(lst);
    setView('catalog');
  };

  const openLesson = async (l) => {
    const mod = modules.find(m => m.id === l.module_id) || activeModule;
    const tier = tierOf(mod);
    if (tier !== 'public' && !user) {
      toast.info(tier === 'account'
        ? 'Créez un compte gratuit pour accéder au Niveau 2.'
        : 'Connectez-vous pour accéder à cette leçon.');
      setView('login');
      return;
    }
    if (user && !isLevelUnlocked(mod)) {
      toast.info('Termine d\'abord le niveau précédent à 100% pour débloquer cette leçon.');
      return;
    }
    if (tier === 'premium' && token) {
      try {
        const access = await apiX('/my-access', 'GET', null, token);
        if (access[l.module_id] === false) {
          const pricings = await apiX('/pricing');
          const p = pricings.find(x => x.module_id === l.module_id) || { module_id: l.module_id };
          const m = modules.find(mm => mm.id === l.module_id) || { id: l.module_id, title_fr: l.module_id };
          setPaywallModule(m); setPaywallPricing(p);
          setView('paywall');
          return;
        }
      } catch (e) { /* fail-open */ }
    }
    const full = await api(`/lessons/${l.id}`);
    setActiveLesson(full);
    if (token) apiX('/continuity', 'POST', { lesson_id: full.id, module_id: full.module_id, step: 1 }, token).catch(() => {});
    setView('lesson');
  };

  const resumeLesson = async (cont) => {
    try {
      const full = await api(`/lessons/${cont.lesson_id}`);
      setActiveLesson(full);
      setView('lesson');
    } catch { toast.error('Leçon introuvable'); }
  };

  return (
    <div className="min-h-screen text-white relative">
      <AnimatedBrandBackground />
      <Toaster richColors position="top-center" />

      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-emerald-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => setView(user ? 'dashboard' : 'home')} className="flex items-center gap-2" data-testid="academy-logo">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-yellow-400 flex items-center justify-center text-slate-950 font-black">J</div>
            <div>
              <div className="font-bold text-sm">JoSmartTech</div>
              <div className="text-[10px] text-yellow-300 tracking-wider">ACADEMY</div>
            </div>
          </button>
          {/* Badge retour vers le site principal josmarttech.net */}
          <a href="https://josmarttech.net" target="_blank" rel="noopener noreferrer" data-testid="main-site-badge"
            className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-400/40 text-[11px] text-white/60 hover:text-emerald-300 transition ml-2"
            title={siteLang === 'fr' ? 'Visiter le site principal JoSmartTech' : 'Visit the main JoSmartTech site'}>
            <Globe size={11} />josmarttech.net
            <ExternalLink size={9} className="opacity-60" />
          </a>
          {/* Bouton Guide — accessible à tout moment */}
          <button onClick={() => setShowGuide(true)} data-testid="open-guide-btn"
            className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-500/10 hover:bg-yellow-500/25 border border-yellow-500/30 hover:border-yellow-400/60 text-[11px] text-yellow-200 hover:text-yellow-100 transition ml-2"
            title={getT(siteLang)('guide_title')}>
            <HelpCircle size={11} />{getT(siteLang)('nav_guide')}
          </button>
          <nav className="flex items-center gap-2 text-sm">
            {user ? (
              <>
                <span className="hidden sm:inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5 text-xs">
                  <Trophy size={14} className="text-yellow-400" /> {user.xp} XP
                  <Flame size={14} className="text-orange-400" /> {user.streak_days || 0}
                  <Heart size={14} className="text-red-400" /> {user.lives}
                </span>
                <button onClick={() => setView('dashboard')} className="px-3 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-xs" data-testid="header-dashboard-btn">{getT(siteLang)('nav_dashboard')}</button>
                <button onClick={() => setView('formation-intro')} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-emerald-500/20 text-xs hidden sm:inline-block" data-testid="header-modules-btn">📚 {getT(siteLang)('nav_modules')}</button>
                <button onClick={() => setView('favorites')} className="px-2 py-1.5 rounded-lg hover:bg-white/10 text-xs" title="Favoris" data-testid="header-fav-btn"><Star size={14} /></button>
                {!user.is_admin && (
                  <button onClick={() => setView('messages')} className="relative px-2 py-1.5 rounded-lg hover:bg-white/10 text-xs" title="Messages" data-testid="header-messages-btn">
                    <Bell size={14} />
                    {unreadMsgs > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-1" data-testid="header-messages-badge">{unreadMsgs}</span>}
                  </button>
                )}
                <button onClick={() => setView('blog')} className="px-3 py-1.5 rounded-lg hover:bg-white/10 text-xs hidden sm:inline-block" data-testid="header-blog-btn">Blog</button>
                <LangSwitcher lang={siteLang} setLang={setSiteLang} />
                <button onClick={() => setView('profile')} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs" data-testid="academy-profile-btn">
                  {user.name}
                </button>
                <button onClick={() => { persistToken(''); setUser(null); setView('home'); }} className="text-xs text-white/60 hover:text-white" data-testid="academy-logout"><LogOut size={14} /></button>
              </>
            ) : (
              <>
                <button onClick={() => setView('home')} className="hidden sm:inline-block px-3 py-1.5 rounded-lg hover:bg-white/10 text-xs text-white/70 hover:text-white" data-testid="nav-home">{siteLang === 'fr' ? 'Accueil' : 'Home'}</button>
                <button onClick={() => setView('about')} className="hidden sm:inline-block px-3 py-1.5 rounded-lg hover:bg-white/10 text-xs text-white/70 hover:text-white" data-testid="nav-about">{siteLang === 'fr' ? 'À propos' : 'About'}</button>
                <button onClick={() => setView('formations-list')} className="hidden sm:inline-block px-3 py-1.5 rounded-lg hover:bg-white/10 text-xs text-white/70 hover:text-white" data-testid="nav-formations">{siteLang === 'fr' ? 'Nos formations' : 'Our Courses'}</button>
                <button onClick={() => setView('blog')} className="hidden sm:inline-block px-3 py-1.5 rounded-lg hover:bg-white/10 text-xs text-white/70 hover:text-white" data-testid="nav-blog">Blog</button>
                <LangSwitcher lang={siteLang} setLang={setSiteLang} />
                <button onClick={() => setView('login')} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-xs" data-testid="academy-login-btn">
                  <LogIn size={14} />{siteLang === 'fr' ? 'Connexion' : 'Sign in'}
                </button>
              </>
            )}
            {/* Admin badge — only visible to logged-in admin users */}
            {user?.is_admin && (
              <button onClick={() => setView('admin')} className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold" data-testid="header-admin-tab">
                <ShieldCheck size={14} />Admin
              </button>
            )}
          </nav>
        </div>
      </header>

      <InstallPWABanner />

      <AnimatePresence mode="wait">
        {view === 'home'         && (user
          ? <Dashboard key="dash0" token={token} user={user} lang={uiLang}
              onResume={resumeLesson}
              onPlayQuick={() => setView('quick')}
              onOpenScenarios={() => setView('scenarios')}
              onOpenListening={() => setView('listening')}
              onOpenReadings={() => setView('readings')}
              onOpenReview={() => setView('review')}
              onOpenAudiobooks={() => setView('audiobooks')}
              onOpenCertificates={() => setView('certificates')} />
          : <Home key="h" modules={modules} setView={setView} lang={siteLang} settings={siteSettings}
              onStartFree={async () => {
                const first = [...modules].sort((a,b)=>(a.order||0)-(b.order||0)).find(m => (m.order||0) <= 1) || [...modules].sort((a,b)=>(a.order||0)-(b.order||0))[0];
                if (first) await openModule(first);
              }}
              onBrowseLevels={() => setView('formation-intro')} />)}
        {view === 'welcome'      && <WelcomeScreen key="w" onChoose={(c) => { if (c === 'english') setView('formation-intro'); else setView('pro-formations'); }} />}
        {view === 'formations-list' && <FormationsListPage key="fl"
            lang={siteLang}
            onBack={() => setView(user ? 'dashboard' : 'home')}
            onOpenPro={() => setView('pro-formations')}
            onOpenEnglish={() => setView('formation-intro')} />}
        {view === 'pro-formations' && <ProFormationsPage key="pf"
            onBack={() => setView(user ? 'dashboard' : 'home')}
            onOpenFormation={() => setView('webai-formation')} />}
        {view === 'webai-formation' && <WebAiFormationPage key="waf"
            onBack={() => setView('pro-formations')} />}
        {view === 'blog'         && <BlogPage key="blog" lang={siteLang} onBack={() => setView(user ? 'dashboard' : 'home')} />}
        {view === 'formation-intro' && <FormationIntro key="fi" modules={modules}
            lang={uiLang}
            levelStatus={levelStatus}
            openAccess={openAccess}
            onBack={() => setView(user ? 'dashboard' : 'home')}
            onStartTest={() => setView('level-test')}
            onModuleClick={async (m) => { await openModule(m); }}
            onStartFromBeginning={async () => {
              const first = modules.sort((a,b)=>(a.order||0)-(b.order||0))[0];
              if (first) await openModule(first);
            }} />}
        {view === 'level-test'   && <LevelTest key="lt"
            onBack={() => setView('formation-intro')}
            onSkip={async () => { const first = modules.sort((a,b)=>(a.order||0)-(b.order||0))[0]; if (first) await openModule(first); }}
            onResult={async () => {
              const first = modules.sort((a,b)=>(a.order||0)-(b.order||0))[0];
              if (first) await openModule(first); else setView('dashboard');
            }} />}
        {view === 'onboarding'   && <Onboarding key="o" token={token} onDone={(opts) => { setUser(prev => ({ ...prev, ...opts, onboarding_done: true })); setView('formation-intro'); }} />}
        {view === 'dashboard'    && <Dashboard key="d" token={token} user={user} lang={uiLang}
            onResume={resumeLesson}
            onPlayQuick={() => setView('quick')}
            onOpenScenarios={() => setView('scenarios')}
            onOpenListening={() => setView('listening')}
            onOpenReadings={() => setView('readings')}
            onOpenReview={() => setView('review')}
            onOpenAudiobooks={() => setView('audiobooks')}
            onOpenCertificates={() => setView('certificates')} />}
        {view === 'quick'        && <QuickSession key="q" token={token} onBack={() => setView('dashboard')} onUserUpdate={setUser} />}
        {view === 'scenarios'    && <ScenariosPage key="sc" token={token} onBack={() => setView('dashboard')} onUserUpdate={setUser} />}
        {view === 'listening'    && <ListeningPlaylist key="ls" onBack={() => setView('dashboard')} />}
        {view === 'readings'     && <Readings key="rd" onBack={() => setView('dashboard')} />}
        {view === 'review'       && <ReviewPage key="rv" token={token} onBack={() => setView('dashboard')} onOpenLesson={openLesson} />}
        {view === 'certificates' && <CertificatesPage key="ct" token={token} user={user} onBack={() => setView('dashboard')} />}
        {view === 'audiobooks'   && <Audiobooks key="ab" token={token} lang={uiLang} onBack={() => setView('dashboard')} />}
        {view === 'favorites'    && <Favorites key="fv" token={token} onBack={() => setView('dashboard')} onOpenLesson={openLesson} lang={uiLang} />}
        {view === 'messages'     && <StudentMessages key="msg" token={token} onBack={() => setView('dashboard')} onRead={refreshUnread} />}
        {view === 'about'        && <AboutPage key="ab" lang={siteLang} onBack={() => setView('home')} />}
        {view === 'faq'          && <FAQPage key="fq" lang={siteLang} onBack={() => setView('home')} />}
        {view === 'terms'        && <TermsPage key="t" lang={siteLang} onBack={() => setView('home')} />}
        {view === 'contact'      && <ContactPage key="ctc" lang={siteLang} onBack={() => setView('home')} />}
        {view === 'catalog'      && <Catalog key="c" mod={activeModule} lessons={lessons} onBack={() => setView(user ? 'dashboard' : 'home')} onOpen={openLesson} user={user} token={token} lang={uiLang} openAccess={openAccess} />}
        {view === 'lesson'       && <Lesson key="l" lesson={activeLesson} user={user} onBack={() => setView('catalog')} token={token} onUserUpdate={setUser} lang={uiLang} />}
        {view === 'paywall'      && <PaywallScreen key="pw" moduleInfo={paywallModule} pricing={paywallPricing} token={token}
              onBack={() => setView(user ? 'dashboard' : 'home')}
              onPaid={() => { setView('catalog'); openModule(paywallModule); }} />}
        {view === 'profile'      && <Profile key="p" user={user} token={token} lang={uiLang} />}
        {view === 'login'        && <Auth key="au" lang={siteLang} onAuth={(t,u,adminToken)=>{
          persistToken(t); setUser(u);
          if (adminToken) {
            persistAdmin(adminToken);
            // Admin lands on the SAME dashboard as students, with an Admin button always accessible in the header
            setView(u.onboarding_done ? 'dashboard' : 'home');
            toast.success(getT(siteLang)('auth_welcome_admin'));
          } else {
            setView(u.onboarding_done ? 'dashboard' : 'onboarding');
          }
        }} />}
        {view === 'admin'        && <AdminPanel key="ad" token={adminToken} setToken={persistAdmin} onRelogin={() => setView('login')} modules={modules} reload={loadModules} onPreviewSite={() => setView(user ? 'dashboard' : 'home')} lang={siteLang} />}
      </AnimatePresence>

      {/* Guide onboarding overlay — visible à la 1ère visite + via bouton Guide du header */}
      {showGuide && <GettingStartedGuide lang={siteLang} onClose={() => { setShowGuide(false); markGuideSeen(); }} />}

      <footer className="border-t border-white/5 mt-12 py-10">
        {(() => { const ft = getT(siteLang); return (
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-yellow-400 flex items-center justify-center text-slate-950 font-black">J</div><div className="font-bold">JoSmartTech Academy</div></div>
            <p className="text-white/50 text-xs">{ft('foot_tagline')}</p>
          </div>
          <div>
            <div className="font-bold text-white/80 mb-2">{ft('foot_navigation')}</div>
            <ul className="space-y-1 text-white/50 text-xs">
              <li><button onClick={() => setView('home')} className="hover:text-emerald-300">{ft('nav_home')}</button></li>
              <li><button onClick={() => setView('about')} className="hover:text-emerald-300" data-testid="footer-about">{ft('nav_about')}</button></li>
              <li><button onClick={() => setView('blog')} className="hover:text-emerald-300" data-testid="footer-blog">Blog</button></li>
              <li><button onClick={() => setView('faq')} className="hover:text-emerald-300" data-testid="footer-faq">FAQ</button></li>
              <li><button onClick={() => setView('contact')} className="hover:text-emerald-300" data-testid="footer-contact">{ft('foot_contact')}</button></li>
              <li><button onClick={() => setView('terms')} className="hover:text-emerald-300" data-testid="footer-terms">{ft('foot_terms')}</button></li>
            </ul>
          </div>
          <div>
            <div className="font-bold text-white/80 mb-2">{ft('foot_levels')}</div>
            <ul className="space-y-1 text-white/50 text-xs">
              <li>🟢 {siteLang === 'en' ? 'Beginner' : 'Débutant'}</li>
              <li>🟡 {siteLang === 'en' ? 'Elementary' : 'Élémentaire'}</li>
              <li>🟠 {siteLang === 'en' ? 'Intermediate' : 'Intermédiaire'}</li>
              <li>🔵 {siteLang === 'en' ? 'Advanced' : 'Avancé'}</li>
              <li>🟣 {siteLang === 'en' ? 'Professional' : 'Professionnel'}</li>
              <li>🔴 Fluent / Expert</li>
            </ul>
          </div>
          <div>
            <div className="font-bold text-white/80 mb-2">{ft('foot_contact')}</div>
            <ul className="space-y-1 text-white/50 text-xs">
              <li>📧 support.josmarttech@gmail.com</li>
              <li>📱 +1 431-334-5083</li>
              <li>🌐 <a href="/" className="hover:text-emerald-300">josmarttech.net</a></li>
            </ul>
            <div className="font-bold text-white/80 mt-5 mb-2">{ft('foot_follow')}</div>
            <div className="flex items-center gap-2" data-testid="academy-socials">
              {(siteSettings?.facebook_url || SOCIALS.facebook) && (
                <a href={siteSettings?.facebook_url || SOCIALS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-blue-500/20 hover:text-blue-300 border border-white/10 hover:border-blue-400/40 flex items-center justify-center text-white/60 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12a12 12 0 1 0-13.9 11.9V15.5h-3v-3.5h3v-2.6c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9v2.2h3.4l-.5 3.5h-2.9v8.4A12 12 0 0 0 24 12z"/></svg>
                </a>
              )}
              {siteSettings?.instagram_url && (
                <a href={siteSettings.instagram_url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-pink-500/20 hover:text-pink-300 border border-white/10 hover:border-pink-400/40 flex items-center justify-center text-white/60 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 2c-3.1 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1zm0 3.4a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8zm0 7.2a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6zm5.6-7.4a1 1 0 1 1-2.1 0 1 1 0 0 1 2 0z"/></svg>
                </a>
              )}
              {(siteSettings?.tiktok_url || SOCIALS.tiktok) && (
                <a href={siteSettings?.tiktok_url || SOCIALS.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="social-tiktok"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-cyan-500/20 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/40 flex items-center justify-center text-white/60 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 6.7a5.5 5.5 0 0 1-3.4-1.2 5.6 5.6 0 0 1-2-3.5h-3.4v13.4a2.7 2.7 0 1 1-2.7-2.8c.3 0 .5 0 .8.1V9.3a6.2 6.2 0 0 0-.8-.1 6.2 6.2 0 1 0 6.2 6.2V9a8.8 8.8 0 0 0 5.3 1.7V7.4l-.1-.7z"/></svg>
                </a>
              )}
              {(siteSettings?.youtube_url || SOCIALS.youtube) && (
                <a href={siteSettings?.youtube_url || SOCIALS.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 border border-white/10 hover:border-red-400/40 flex items-center justify-center text-white/60 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/></svg>
                </a>
              )}
              {siteSettings?.linkedin_url && (
                <a href={siteSettings.linkedin_url} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="social-linkedin"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-sky-500/20 hover:text-sky-300 border border-white/10 hover:border-sky-400/40 flex items-center justify-center text-white/60 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22.2 0H1.8C.8 0 0 .8 0 1.7v20.5C0 23.2.8 24 1.8 24h20.5c1 0 1.8-.8 1.8-1.8V1.8c0-1-.8-1.8-1.8-1.8zM7.1 20.5H3.6V9h3.6v11.5zM5.3 7.4a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2zm15.2 13.1H17V14.9c0-1.3 0-3.1-1.9-3.1s-2.2 1.5-2.2 3v5.7H9.4V9h3.4v1.6h.1a3.7 3.7 0 0 1 3.4-1.8c3.6 0 4.3 2.4 4.3 5.5v6.2z"/></svg>
                </a>
              )}
              {(!siteSettings?.facebook_url && !siteSettings?.instagram_url && !siteSettings?.tiktok_url && !siteSettings?.youtube_url && !siteSettings?.linkedin_url) && (
                <span className="text-xs text-white/30 italic">{ft('foot_no_socials')}</span>
              )}
            </div>
          </div>
        </div>
        ); })()}
        <div className="text-center text-white/30 text-xs mt-8">© {new Date().getFullYear()} JoSmartTech Academy · {getT(siteLang)('foot_rights')} · {getT(siteLang)('foot_brand_of')} JoSmartTech</div>
      </footer>
    </div>
  );
}

// === HOME (guest landing — style site principal : seulement les titres des formations) ===
function Home({ modules, setView, lang = 'fr', settings = {}, onStartFree, onBrowseLevels }) {
  const t = getT(lang);
  const [pricing, setPricing] = useState({});
  useEffect(() => {
    apiX('/pricing').then(items => {
      const m = {}; items.forEach(p => { m[p.module_id] = p; }); setPricing(m);
    }).catch(()=>{});
  }, []);

  const features = [
    { Icon: BookOpen,    title: t('feat_method_title'), desc: t('feat_method_desc') },
    { Icon: Volume2,     title: t('feat_audio_title'),  desc: t('feat_audio_desc') },
    { Icon: Trophy,      title: t('feat_gami_title'),   desc: t('feat_gami_desc') },
    { Icon: Award,       title: t('feat_certs_title'),  desc: t('feat_certs_desc') },
    { Icon: Sparkles,    title: t('feat_ai_title'),     desc: t('feat_ai_desc') },
    { Icon: ShieldCheck, title: t('feat_track_title'),  desc: t('feat_track_desc') },
  ];

  const testimonials = [
    { name: t('testim_1_name'), role: t('testim_1_role'), text: t('testim_1_text'), rating: 5 },
    { name: t('testim_2_name'), role: t('testim_2_role'), text: t('testim_2_text'), rating: 5 },
    { name: t('testim_3_name'), role: t('testim_3_role'), text: t('testim_3_text'), rating: 5 },
  ];

  const faqs = [
    { q: t('faq_q1'),  a: t('faq_a1')  },
    { q: t('faq_q2'),  a: t('faq_a2')  },
    { q: t('faq_q3'),  a: t('faq_a3')  },
    { q: t('faq_q4'),  a: t('faq_a4')  },
    { q: t('faq_q5'),  a: t('faq_a5')  },
    { q: t('faq_q6'),  a: t('faq_a6')  },
    { q: t('faq_q7'),  a: t('faq_a7')  },
    { q: t('faq_q8'),  a: t('faq_a8')  },
    { q: t('faq_q9'),  a: t('faq_a9')  },
    { q: t('faq_q10'), a: t('faq_a10') },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-7xl mx-auto px-4">
      {/* HERO */}
      <section className="py-16 lg:py-24 text-center">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 text-xs text-emerald-300 mb-4">
            <Sparkles size={12} />{t('hero_badge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-emerald-300 via-yellow-300 to-emerald-200 bg-clip-text text-transparent">JoSmartTech Academy</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 mt-4 max-w-2xl mx-auto">{t('hero_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <button onClick={() => onStartFree && onStartFree()} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 rounded-full shadow-lg shadow-emerald-500/20" data-testid="hero-start-free">
              🚀 {lang === 'fr' ? 'Commencer gratuitement' : 'Start for free'}
            </button>
            <button onClick={() => setView('formations-list')} className="bg-white/10 hover:bg-white/20 px-6 py-3 rounded-full" data-testid="hero-formations">{t('hero_cta_discover')}</button>
          </div>
        </motion.div>
      </section>

      {/* FORMATIONS */}
      <section id="formations" className="py-8">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><BookOpen size={20} className="text-emerald-400" />{t('formations_title')}</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <motion.button whileHover={{ y: -4 }} onClick={() => setView('formation-intro')} data-testid="formation-english"
            className="text-left rounded-3xl p-8 bg-gradient-to-br from-emerald-500 to-emerald-700 relative overflow-hidden group">
            <div className="absolute -right-4 -bottom-4 text-6xl opacity-10 group-hover:opacity-20 transition leading-none">🇬🇧</div>
            <Mic size={36} className="text-white/90 mb-4" />
            <div className="text-xs uppercase tracking-wider text-white/70">{t('formation_english_tag')}</div>
            <h3 className="text-2xl font-bold mt-1">{t('formation_english_title')}</h3>
            <p className="text-white/80 mt-2 text-sm">{t('formation_english_desc')}</p>
            <div className="inline-flex items-center gap-1 mt-5 text-sm font-bold bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">{t('formation_english_cta')}<ChevronRight size={14} /></div>
          </motion.button>

          <motion.button whileHover={{ y: -4 }} data-testid="formation-pro" onClick={() => setView('pro-formations')}
            className="text-left rounded-3xl p-8 bg-gradient-to-br from-blue-700 to-purple-800 relative overflow-hidden group">
            <div className="absolute -right-6 -bottom-6 text-9xl opacity-10 group-hover:opacity-20 transition">🎓</div>
            <GraduationCap size={36} className="text-white/90 mb-4" />
            <div className="text-xs uppercase tracking-wider text-white/70">{t('formation_pro_tag')}</div>
            <h3 className="text-2xl font-bold mt-1">{t('formation_pro_title')}</h3>
            <p className="text-white/80 mt-2 text-sm">{t('formation_pro_desc')}</p>
            <div className="inline-flex items-center gap-1 mt-5 text-sm font-bold bg-yellow-400 text-slate-900 px-3 py-1.5 rounded-full">{t('formation_pro_cta')}</div>
          </motion.button>
        </div>
      </section>

      {/* ESSENTIAL FEATURES */}
      <section className="py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold">{t('features_title')}</h2>
          <p className="text-white/60 text-sm mt-2">{t('features_sub')}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map(({Icon, title, desc}) => (
            <motion.div key={title} whileHover={{ y: -3 }} className="bg-gradient-to-br from-white/5 to-white/0 rounded-2xl p-5 border border-white/10 hover:border-emerald-400/30 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500/20 to-yellow-500/10 flex items-center justify-center mb-3">
                <Icon size={20} className="text-emerald-300" />
              </div>
              <div className="font-bold text-base">{title}</div>
              <div className="text-sm text-white/60 mt-1.5 leading-relaxed">{desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16" data-testid="testimonials-section">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1 text-yellow-300 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold">{t('testim_title')}</h2>
          <p className="text-white/60 text-sm mt-2">{t('testim_sub')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((tt, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-gradient-to-br from-emerald-500/10 to-yellow-500/5 rounded-2xl p-6 border border-emerald-400/20" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-1 text-yellow-400 mb-3">
                {[...Array(tt.rating)].map((_, k) => <Star key={k} size={14} fill="currentColor" />)}
              </div>
              <p className="text-white/85 text-sm leading-relaxed italic">« {tt.text} »</p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="font-bold text-emerald-300 text-sm">{tt.name}</div>
                <div className="text-xs text-white/50">{tt.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FaqSection faqs={faqs} t={t} />

      {/* FINAL CTA */}
      <section className="py-16 text-center">
        <div className="bg-gradient-to-br from-emerald-500/15 to-yellow-500/10 border border-emerald-400/30 rounded-3xl p-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">{t('cta_title')}</h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">{t('cta_sub')}</p>
          <button onClick={() => setView('login')} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-8 py-3 rounded-full" data-testid="cta-final">{t('cta_btn')}</button>
        </div>
      </section>

      {/* MOTIVATION SECTION */}
      <section className="py-16" data-testid="academy-motivation-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3 font-semibold">Motivation</div>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">
            {t('founder_quote')} <span className="bg-gradient-to-r from-emerald-300 to-yellow-300 bg-clip-text text-transparent">{t('founder_quote_em')}</span>. »
          </h2>
          <p className="text-emerald-200 italic text-lg leading-relaxed">
            {t('founder_p4_a')}<strong>{t('founder_p4_strong')}</strong>.
          </p>
        </div>
      </section>
    </motion.div>
  );
}

function FaqSection({ faqs, t }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? faqs : faqs.slice(0, 4);
  return (
    <section className="py-16" data-testid="faq-section">
      <div className="text-center mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold">{t ? t('faq_title') : 'Questions fréquentes'}</h2>
        <p className="text-white/60 text-sm mt-2">{t ? t('faq_sub') : 'Tout ce que vous devez savoir avant de commencer'}</p>
      </div>
      <div className="max-w-3xl mx-auto space-y-3">
        {visible.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} index={i} />)}
      </div>
      {faqs.length > 4 && (
        <div className="text-center mt-6">
          <button onClick={() => setShowAll(s => !s)} data-testid="faq-toggle"
            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 px-6 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2 transition">
            {showAll ? (t ? t('faq_less') : 'Voir moins') : `${t ? t('faq_more') : 'Voir plus'} (${faqs.length - 4} ${t ? t('faq_more_n') : 'de plus'})`}
            <ChevronRight size={14} className={`transition-transform ${showAll ? '-rotate-90' : 'rotate-90'}`} />
          </button>
        </div>
      )}
    </section>
  );
}

function LangSwitcher({ lang, setLang }) {
  return (
    <div className="hidden sm:inline-flex items-center bg-white/5 border border-white/10 rounded-full overflow-hidden text-[11px]" data-testid="lang-switcher">
      <button onClick={() => setLang('fr')} data-testid="lang-fr"
        className={`px-2.5 py-1 font-bold transition ${lang === 'fr' ? 'bg-emerald-500 text-slate-950' : 'text-white/60 hover:text-white'}`}>FR</button>
      <button onClick={() => setLang('en')} data-testid="lang-en"
        className={`px-2.5 py-1 font-bold transition ${lang === 'en' ? 'bg-emerald-500 text-slate-950' : 'text-white/60 hover:text-white'}`}>EN</button>
    </div>
  );
}


function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
      className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)} data-testid={`faq-${index}`}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5">
        <span className="font-semibold text-sm">{q}</span>
        <ChevronRight size={16} className={`flex-shrink-0 text-emerald-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
          className="px-5 pb-4 text-sm text-white/70 leading-relaxed">{a}</motion.div>
      )}
    </motion.div>
  );
}

// === CATALOG (avec verrouillage progressif + favoris) ===
function Catalog({ mod, lessons, onBack, onOpen, user, token, lang = 'fr', openAccess = false }) {
  const fr = lang !== 'en';
  const [completedIds, setCompletedIds] = useState(new Set());
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    if (!token) {
      try {
        const arr = JSON.parse(localStorage.getItem('jstGuestProgress') || '[]');
        setCompletedIds(new Set(arr.filter(i => i.step === 4).map(i => i.lesson_id)));
      } catch { /* ignore */ }
      return;
    }
    apiX('/progress', 'GET', null, token).then(items => {
      const done = new Set(items.filter(i => i.step === 4).map(i => i.lesson_id));
      setCompletedIds(done);
    }).catch(()=>{});
    apiX('/favorites', 'GET', null, token).then(list => {
      setFavIds(new Set(list.map(l => l.id)));
    }).catch(()=>{});
  }, [token]);

  const completedCount = lessons.filter(l => completedIds.has(l.id)).length;
  const pct = lessons.length ? Math.round(completedCount / lessons.length * 100) : 0;

  // Sequential unlock: lesson N unlocked if lesson N-1 completed (or first lesson)
  const isUnlocked = (i) => {
    if (openAccess) return true;
    if (!user) return true; // guest can preview
    if (i === 0) return true;
    const prev = lessons[i - 1];
    return completedIds.has(prev?.id);
  };

  const toggleFav = async (e, lessonId) => {
    e.stopPropagation();
    if (!token) return toast.info('Connectez-vous pour ajouter des favoris');
    try {
      if (favIds.has(lessonId)) {
        await apiX(`/favorites/${lessonId}`, 'DELETE', null, token);
        setFavIds(prev => { const n = new Set(prev); n.delete(lessonId); return n; });
      } else {
        await apiX('/favorites', 'POST', { lesson_id: lessonId }, token);
        setFavIds(prev => new Set(prev).add(lessonId));
      }
    } catch (err) { toast.error(err.message); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-4 py-10">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">{mod?.icon}</span>
        <h2 className="text-2xl font-bold">{mod?.title_fr}</h2>
      </div>
      <p className="text-white/60 text-sm mb-4">{mod?.description_fr}</p>

      {mod?.intro_en && (
        <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-2xl p-4 mb-6" data-testid="module-intro-en">
          <div className="flex items-start gap-3">
            <button onClick={() => speak(mod.intro_en, 'en-US')} data-testid="module-intro-listen"
              className="mt-0.5 p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 flex-shrink-0" title="Écouter en anglais">
              <Volume2 size={16} className="text-emerald-300" />
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Introduction · English 🎧</div>
              <p className="text-sm text-white/85 leading-relaxed">{mod.intro_en}</p>
            </div>
          </div>
        </div>
      )}
      {user && lessons.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-6 flex items-center gap-3">
          <Trophy size={16} className="text-yellow-400" />
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs text-white/70 mb-1"><span>Progression du parcours</span><span>{completedCount}/{lessons.length}</span></div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-yellow-400" style={{ width: `${pct}%` }} /></div>
          </div>
          <span className="text-sm font-bold text-emerald-300">{pct}%</span>
        </div>
      )}

      {lessons.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-sm text-yellow-200">
          Aucune leçon publiée pour le moment. L'admin doit en générer via le panneau Admin.
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {lessons.map((l, i) => {
          const unlocked = isUnlocked(i);
          const completed = completedIds.has(l.id);
          const fav = favIds.has(l.id);
          return (
            <motion.div key={l.id} whileHover={unlocked ? { y: -2 } : {}} data-testid={`academy-lesson-${l.id}`}
              onClick={() => unlocked ? onOpen(l) : toast.info('Termine la leçon précédente pour débloquer celle-ci.')}
              className={`relative cursor-pointer text-left rounded-xl p-4 transition border ${
                completed ? 'bg-emerald-500/15 border-emerald-500/40'
                : unlocked ? 'bg-white/5 hover:bg-white/10 border-white/10'
                : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
              }`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold ${completed ? 'text-emerald-300' : 'text-emerald-400'}`}>
                  {completed ? <><CheckCircle size={12} className="inline" /> {fr ? 'Terminée' : 'Completed'}</> : `${fr ? 'Leçon' : 'Lesson'} ${i + 1}`}
                </span>
                <span className="text-[10px] text-white/40">{l.level}</span>
              </div>
              <div className="font-bold mt-2 pr-6">{l.title}</div>
              <div className="mt-3 text-xs text-white/50 flex items-center gap-2">
                {!unlocked ? <><Lock size={12} />{fr ? 'Verrouillée' : 'Locked'}</> : (l.kind === 'conjugation' || l.content?.type === 'conjugation') ? <><BookOpen size={12} />{fr ? 'Référence + exercices' : 'Reference + exercises'}</> : <><BookOpen size={12} />{fr ? '4 étapes' : '4 steps'}</>}
              </div>
              {user && (
                <button onClick={(e) => toggleFav(e, l.id)} data-testid={`fav-${l.id}`}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10">
                  <Star size={14} className={fav ? 'text-yellow-400 fill-yellow-400' : 'text-white/30'} />
                </button>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// === Guest progress (avant création de compte) ===
const GUEST_KEY = 'jstGuestProgress';
const saveGuestProgress = (lesson_id, step, score) => {
  try {
    const arr = JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
    arr.push({ lesson_id, step, score });
    localStorage.setItem(GUEST_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
};
const syncGuestProgress = async (tok) => {
  try {
    const arr = JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
    if (!arr.length) return;
    for (const p of arr) { await api('/progress', 'POST', p, tok).catch(() => {}); }
    localStorage.removeItem(GUEST_KEY);
  } catch { /* ignore */ }
};

// === LESSON (4 étapes) ===
function ConjugationLesson({ lesson, user, token, onBack, lang = 'fr' }) {
  const [completed, setCompleted] = useState(false);
  const complete = async () => {
    if (token) { try { await api('/progress', 'POST', { lesson_id: lesson.id, step: 4, score: 100 }, token); } catch (e) { /* ignore */ } }
    else { saveGuestProgress(lesson.id, 4, 100); }
    setCompleted(true);
  };
  if (completed) {
    return <LessonCompleteScreen lessonTitle={lesson.title} xpGained={0} learnedItems={[]} user={user} onContinue={onBack} onRestart={() => setCompleted(false)} />;
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <button onClick={onBack} className="text-white/60 hover:text-white text-sm flex items-center gap-1" data-testid="conjugation-exit"><ChevronLeft size={16} />{lang === 'fr' ? 'Quitter' : 'Exit'}</button>
      </div>
      <Conjugation embedded lang={lang} onComplete={complete} />
    </motion.div>
  );
}

function Lesson({ lesson, user, onBack, token, onUserUpdate, lang = 'fr' }) {
  const t = getT(lang);
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(false);
  const [totalXp, setTotalXp] = useState(0);
  const [xpToast, setXpToast] = useState({ show: false, amount: 0 });
  const [immersion, setImmersion] = useState(false);
  // Parcours unique : francophone qui apprend l'anglais (FR → EN).
  const direction = 'fr2en';
  // Tutor modals
  const [tutorWord, setTutorWord] = useState({ open: false, word: '', context: '', prestored: null });
  const [tutorChatOpen, setTutorChatOpen] = useState(false);

  // Étape 0 = "Cours" (théorie/grammaire) si disponible
  useEffect(() => {
    const cc = lesson?.content || {};
    if (cc.cours && Array.isArray(cc.cours.sections) && cc.cours.sections.length) setStep(0);
    else setStep(1);
  }, [lesson?.id]);

  if (!lesson) return null;
  const c = lesson.content || {};
  if (c.type === 'conjugation' || lesson.kind === 'conjugation') {
    return <ConjugationLesson lesson={lesson} user={user} token={token} onBack={onBack} lang={lang} />;
  }
  const hasCourse = !!(c.cours && Array.isArray(c.cours.sections) && c.cours.sections.length);
  const hasReading = !!(c.reading && c.reading.text && Array.isArray(c.reading.questions) && c.reading.questions.length);
  const maxStep = hasReading ? 5 : 4;
  const base = hasCourse ? [0, 1, 2, 3, 4] : [1, 2, 3, 4];
  const stepsList = hasReading ? [...base, 5] : base;
  const mirror = c.mirror || {};
  const step2Items = c.step2_exercises || [];
  const step3Items = c.step3_minichat || [];
  const mirrorMissing = false;

  const finishStep = async (score) => {
    if (token) {
      try {
        const r = await api('/progress', 'POST', { lesson_id: lesson.id, step, score }, token);
        onUserUpdate(prev => prev ? { ...prev, xp: r.total_xp } : prev);
        setTotalXp(prev => prev + (r.xp_gained || 0));
        setXpToast({ show: true, amount: r.xp_gained || 0 });
      } catch (e) { console.error(e); }
    } else {
      saveGuestProgress(lesson.id, step, score);
    }
    if (step < maxStep) setStep(step + 1);
    else {
      if (token) { try { await api('/srs/seed', 'POST', { lesson_id: lesson.id }, token); } catch { /* ignore */ } }
      setCompleted(true);
    }
  };

  if (completed) {
    const learned = ((c.step1_lecture?.items) || []).slice(0, 5).map(it => `${it.tgt} → ${it.src}`);
    return (
      <>
        <LessonCompleteScreen
          lessonTitle={lesson.title}
          xpGained={totalXp}
          learnedItems={learned}
          user={user}
          onContinue={onBack}
          onRestart={() => { setCompleted(false); setTotalXp(0); setStep(1); }} />
        <XpGainToast amount={xpToast.amount} show={xpToast.show} onDone={() => setXpToast({ show: false, amount: 0 })} />
      </>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className={immersion ? "fixed inset-0 z-40 bg-slate-950 overflow-y-auto" : "max-w-3xl mx-auto px-4 py-8"}>
      <div className={immersion ? "max-w-3xl mx-auto px-4 py-6" : ""}>
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white/60 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} />{t('lesson_exit')}</button>
          <button onClick={() => setImmersion(!immersion)} data-testid="toggle-immersion"
            className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-1.5">
            {immersion ? t('lesson_mode_normal') : t('lesson_mode_immersion')}
          </button>
        </div>
        <h2 className="text-2xl font-bold mb-4">{lesson.title}</h2>
        <div className="flex gap-2 mb-8">
          {stepsList.map(s => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-emerald-400' : 'bg-white/10'}`} />
          ))}
        </div>
        {step === 0 && <CourseTheory data={c.cours} lang={lang} onDone={() => setStep(1)} />}
        {step === 1 && <Step1Lecture data={c.step1_lecture} direction={direction} onDone={() => finishStep(100)} lang={lang} onAskTutor={(word, context, prestored) => setTutorWord({ open: true, word, context, prestored })} />}
        {step === 2 && <Step2Exercises items={step2Items} direction={direction} onDone={(s) => finishStep(s)} lang={lang} />}
        {step === 3 && <Step3MiniChat items={step3Items} direction={direction} onDone={() => finishStep(100)} lang={lang} />}
        {step === 4 && <Step4Audio items={c.step4_audio || []} direction={direction} onDone={() => finishStep(100)} lang={lang} token={token} />}
        {step === 5 && <ReadingComprehension data={c.reading} lang={lang} onDone={(s) => finishStep(s)} />}
      </div>
      <XpGainToast amount={xpToast.amount} show={xpToast.show} onDone={() => setXpToast({ show: false, amount: 0 })} />

      {/* Floating Coach button */}
      <button onClick={() => setTutorChatOpen(true)}
        className="fixed bottom-6 right-6 z-30 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-4 py-3 rounded-full shadow-xl shadow-emerald-500/30 transition flex items-center gap-2 group"
        data-testid="open-tutor-chat">
        <Sparkles size={16} />
        <span className="hidden sm:inline">{lang === 'fr' ? 'Coach' : 'Coach'}</span>
        <span className="sm:hidden">?</span>
      </button>

      {/* Tutor modals */}
      <AcademyTutorWordModal
        open={tutorWord.open}
        onClose={() => setTutorWord({ open: false, word: '', context: '', prestored: null })}
        word={tutorWord.word}
        lessonId={lesson.id}
        lessonTitle={lesson.title}
        direction={direction}
        context={tutorWord.context}
        prestored={tutorWord.prestored}
        lang={lang}
      />
      <AcademyTutorChat
        open={tutorChatOpen}
        onClose={() => setTutorChatOpen(false)}
        lessonId={lesson.id}
        lessonTitle={lesson.title}
        lessonItems={(c.step1_lecture?.items) || []}
        direction={direction}
        lang={lang}
      />
    </motion.div>
  );
}

function ReadingComprehension({ data, lang = 'fr', onDone }) {
  const d = data || {};
  const questions = d.questions || [];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = questions.length > 0 && questions.every((_, i) => answers[i] != null);
  const score = submitted
    ? Math.round((questions.filter((q, i) => answers[i] === q.answer).length / questions.length) * 100)
    : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="lesson-reading" className="space-y-5">
      <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 text-xs text-emerald-300">
        <BookOpen size={12} />{lang === 'fr' ? 'Lecture & compréhension' : 'Reading & comprehension'}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          {d.title && <h3 className="font-bold text-lg text-emerald-200">{d.title}</h3>}
          <button onClick={() => speak(d.text, 'en-US')} data-testid="reading-listen"
            className="p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 flex items-center gap-1.5 text-xs text-emerald-200">
            <Volume2 size={14} />{lang === 'fr' ? 'Écouter' : 'Listen'}
          </button>
        </div>
        <p className="text-white/85 leading-relaxed whitespace-pre-line" data-testid="reading-text">{d.text}</p>
      </div>

      <div className="space-y-4">
        {questions.map((q, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`reading-q-${i}`}>
            <div className="font-semibold mb-3 text-sm">{i + 1}. {q.q}</div>
            <div className="space-y-2">
              {(q.options || []).map((opt, k) => {
                const chosen = answers[i] === opt;
                const correct = submitted && opt === q.answer;
                const wrong = submitted && chosen && opt !== q.answer;
                return (
                  <button key={k} disabled={submitted}
                    onClick={() => setAnswers(a => ({ ...a, [i]: opt }))}
                    data-testid={`reading-q-${i}-opt-${k}`}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm border transition ${
                      correct ? 'bg-emerald-500/25 border-emerald-400 text-emerald-100' :
                      wrong ? 'bg-red-500/20 border-red-400 text-red-100' :
                      chosen ? 'bg-emerald-500/15 border-emerald-400/50' :
                      'bg-slate-900/50 border-white/10 hover:border-emerald-400/30'}`}>
                    {opt}{correct && ' ✓'}{wrong && ' ✗'}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!submitted ? (
        <button onClick={() => setSubmitted(true)} disabled={!allAnswered} data-testid="reading-submit"
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl">
          {lang === 'fr' ? 'Vérifier mes réponses' : 'Check my answers'}
        </button>
      ) : (
        <div className="space-y-3">
          <div className={`text-center rounded-2xl p-4 font-bold ${score >= 60 ? 'bg-emerald-500/15 text-emerald-200' : 'bg-amber-500/15 text-amber-200'}`} data-testid="reading-score">
            {lang === 'fr' ? 'Score' : 'Score'} : {score}% — {score >= 60 ? (lang === 'fr' ? 'Bravo !' : 'Well done!') : (lang === 'fr' ? 'Continue à t\'entraîner !' : 'Keep practising!')}
          </div>
          <button onClick={() => onDone(score)} data-testid="reading-continue"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">
            {lang === 'fr' ? 'Terminer la leçon' : 'Finish the lesson'} <ChevronRight size={16} />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function CourseTheory({ data, lang = 'fr', onDone }) {
  const d = data || {};
  const sections = d.sections || [];
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="lesson-course-theory" className="space-y-5">
      <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 text-xs text-emerald-300">
        <BookOpen size={12} />{lang === 'fr' ? 'Le cours' : 'The lesson'}
      </div>
      {d.intro && <p className="text-white/80 leading-relaxed">{d.intro}</p>}

      {sections.map((s, i) => (
        <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`course-section-${i}`}>
          {s.heading && <h3 className="font-bold text-lg text-emerald-200 mb-2">{s.heading}</h3>}
          {s.explanation && <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line">{s.explanation}</p>}
          {Array.isArray(s.examples) && s.examples.length > 0 && (
            <div className="mt-3 space-y-2">
              {s.examples.map((ex, k) => (
                <div key={k} className="flex items-start gap-3 bg-slate-900/60 rounded-lg p-3" data-testid={`course-example-${i}-${k}`}>
                  <button onClick={() => speak(ex.en, 'en-US')} className="mt-0.5 p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 flex-shrink-0" data-testid={`course-speak-${i}-${k}`}>
                    <Volume2 size={14} className="text-emerald-300" />
                  </button>
                  <div className="min-w-0">
                    <div className="font-semibold text-white">{ex.en}</div>
                    {ex.fr && <div className="text-sm text-white/50">{ex.fr}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {d.tip && (
        <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-2xl p-4 text-sm text-yellow-100" data-testid="course-tip">
          💡 <span className="font-semibold">{lang === 'fr' ? 'Astuce' : 'Tip'} :</span> {d.tip}
        </div>
      )}

      <button onClick={onDone} data-testid="course-continue"
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2">
        {lang === 'fr' ? "J'ai lu le cours — passer à la pratique" : "I've read the lesson — start practice"} <ChevronRight size={16} />
      </button>
    </motion.div>
  );
}

function Step1Lecture({ data, direction = 'fr2en', onDone, lang = 'fr', onAskTutor }) {
  const t = getT(lang);
  const items = (data && data.items) || [];
  const targetLang = direction === 'en2fr' ? 'fr-FR' : 'en-US';
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-2 text-xs text-emerald-300 uppercase tracking-wider mb-4"><BookOpen size={14} />{t('lesson_step1_label')}</div>
      <h3 className="font-bold text-lg mb-4">{data?.title || t('lesson_learn_keys')}</h3>
      <div className="space-y-3">
        {items.map((it, i) => {
          const big = direction === 'en2fr' ? it.src : it.tgt;
          const small = direction === 'en2fr' ? it.tgt : it.src;
          return (
            <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/10">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-emerald-300">{big}</div>
                <div className="text-sm text-white/60">{small}</div>
                {it.note && <div className="text-[11px] text-white/40 mt-0.5 italic">{it.note}</div>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {onAskTutor && (
                  <button
                    onClick={() => onAskTutor(it.explain?.word || big, small, it.explain || null)}
                    className="p-2 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/30 border border-cyan-400/20 transition group"
                    title="Explain in detail"
                    data-testid={`tutor-explain-${i}`}
                  >
                    <Sparkles size={14} className="text-cyan-300 group-hover:scale-110 transition-transform" />
                  </button>
                )}
                <button onClick={() => speak(big, targetLang)} className="p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30" data-testid={`speak-${i}`}>
                  <Volume2 size={16} className="text-emerald-300" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {onAskTutor && (
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-cyan-300/70">
          <Sparkles size={11} />
          {lang === 'fr' ? "Clique sur ✨ pour avoir une explication détaillée d'un mot" : 'Click ✨ for a detailed explanation of a word'}
        </div>
      )}
      <button onClick={onDone} className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="step1-continue">
        {t('lesson_continue')}<ChevronRight size={16} />
      </button>
    </div>
  );
}

function Step2Exercises({ items, direction = 'fr2en', onDone, lang = 'fr' }) {
  const t = getT(lang);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [correct, setCorrect] = useState(0);
  const ex = items[idx];
  if (!ex) return null;

  const check = (chosen) => {
    const raw = chosen !== undefined ? chosen : answer;
    const a = (raw || '').trim().toLowerCase();
    const ok = a === (ex.answer || '').trim().toLowerCase();
    setFeedback(ok ? 'ok' : 'ko');
    if (ok) setCorrect(c => c + 1);
    setTimeout(() => {
      setFeedback(null); setAnswer('');
      if (idx < items.length - 1) setIdx(idx + 1);
      else onDone(Math.round((correct + (ok ? 1 : 0)) / items.length * 100));
    }, 1200);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs text-emerald-300 uppercase tracking-wider"><MessageCircle size={14} />{t('lesson_step2_label')}</div>
        <div className="text-xs text-white/50">{idx + 1} / {items.length}</div>
      </div>
      <div className="text-sm text-white/60 mb-2">{ex.type === 'fill' && t('lesson_ex_fill')}{ex.type === 'translate' && t('lesson_ex_translate')}{ex.type === 'qcm' && t('lesson_ex_qcm')}{ex.type === 'correct' && t('lesson_ex_correct_type')}</div>
      <div className="text-lg font-bold mb-4">{ex.question}</div>
      {ex.type === 'qcm' ? (
        <div className="space-y-2">
          {(ex.options || []).map((opt, i) => (
            <button key={i} onClick={() => { setAnswer(opt); check(opt); }}
              className={`w-full text-left p-3 rounded-lg border transition ${answer === opt ? 'border-emerald-400 bg-emerald-500/20' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
              data-testid={`qcm-opt-${i}`}>{opt}</button>
          ))}
        </div>
      ) : (
        <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder={t('lesson_your_answer')} onKeyDown={e => e.key === 'Enter' && check()}
          className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30" data-testid="exercise-input" />
      )}
      {ex.hint && <div className="text-xs text-white/40 mt-2">💡 {ex.hint}</div>}
      {feedback && (
        <div className={`mt-4 p-3 rounded-lg ${feedback === 'ok' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
          {feedback === 'ok' ? <><CheckCircle size={16} className="inline mr-2" />{t('lesson_correct')}</> : <><XCircle size={16} className="inline mr-2" />{t('lesson_answer_label')} <strong>{ex.answer}</strong></>}
        </div>
      )}
      {ex.type !== 'qcm' && !feedback && (
        <button onClick={() => check()} disabled={!answer} className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl" data-testid="exercise-check">{t('lesson_submit')}</button>
      )}
    </div>
  );
}

function Step3MiniChat({ items, direction = 'fr2en', onDone, lang = 'fr' }) {
  const t = getT(lang);
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(false);
  const it = items[idx];
  const ttsLang = direction === 'en2fr' ? 'fr-FR' : 'en-US';
  if (!it) return <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-white/60">{t('lesson_no_chat')}<button onClick={onDone} className="block mx-auto mt-4 bg-emerald-500 text-slate-950 font-bold py-2 px-6 rounded-lg">{t('lesson_next')}</button></div>;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs text-emerald-300 uppercase tracking-wider"><MessageCircle size={14} />{t('lesson_step3_label')}</div>
        <div className="text-xs text-white/50">{idx + 1} / {items.length}</div>
      </div>
      <div className="bg-slate-800/60 rounded-2xl rounded-tl-sm p-4 mb-3">
        <div className="text-xs text-white/40 mb-1">{t('lesson_question_label')}</div>
        <div className="text-base">{it.q}</div>
      </div>
      {shown && (
        <div className="bg-emerald-500/15 rounded-2xl rounded-tr-sm p-4 mb-3 border border-emerald-500/30">
          <div className="text-xs text-emerald-300 mb-1">{t('lesson_answer_label').replace(':','').trim()}</div>
          <div className="text-base font-bold flex items-center gap-2">
            {it.a}
            <button onClick={() => speak(it.a, ttsLang)}><Volume2 size={14} className="text-emerald-300" /></button>
          </div>
        </div>
      )}
      {!shown ? (
        <button onClick={() => setShown(true)} className="w-full bg-white/10 hover:bg-white/20 py-3 rounded-xl" data-testid="reveal-answer">{t('lesson_reveal')}</button>
      ) : (
        <button onClick={() => { setShown(false); if (idx < items.length - 1) setIdx(idx + 1); else onDone(); }}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="step3-next">
          {idx < items.length - 1 ? t('lesson_next') : t('lesson_continue')}<ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}

function PronunciationCheck({ target, lang = 'en-US', token }) {
  const [state, setState] = useState('idle'); // idle | recording | processing | result
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);

  const stop = () => {
    clearTimeout(stopTimerRef.current);
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setState('processing');
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const b64 = await new Promise(res => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(blob); });
        try {
          const r = await apiX('/pronounce', 'POST', { target_text: target, audio_base64: b64, lang: (lang || 'en').slice(0, 2) }, token);
          setResult(r); setState('result');
        } catch { setResult({ score: null }); setState('result'); }
      };
      mr.start();
      setState('recording');
      stopTimerRef.current = setTimeout(stop, 6000);
    } catch {
      setResult({ score: null, error: 'mic' }); setState('result');
    }
  };

  const reset = () => { setResult(null); setState('idle'); };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid="pronunciation-check">
      {state === 'idle' && (
        <button onClick={start} data-testid="pron-record"
          className="w-full bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/30 text-yellow-100 font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm">
          <Mic size={15} />🎤 Vérifier ma prononciation
        </button>
      )}
      {state === 'recording' && (
        <button onClick={stop} data-testid="pron-stop"
          className="w-full bg-red-500 hover:bg-red-400 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm animate-pulse">
          <Mic size={15} />Enregistrement… (touche pour arrêter)
        </button>
      )}
      {state === 'processing' && (
        <div className="text-center text-white/60 text-sm py-2 flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Analyse de ta prononciation…</div>
      )}
      {state === 'result' && (
        <div data-testid="pron-result" className="text-center">
          {result?.score == null ? (
            <div className="text-amber-300 text-sm">{result?.error === 'mic' ? "Micro non autorisé. Active le micro pour t'entraîner." : 'Analyse indisponible, réessaie.'}</div>
          ) : (
            <>
              <div className={`text-3xl font-black ${result.score >= 85 ? 'text-emerald-300' : result.score >= 60 ? 'text-yellow-300' : 'text-red-300'}`} data-testid="pron-score">{result.score}%</div>
              {result.transcript && <div className="text-xs text-white/50 mt-1">Entendu : « {result.transcript} »</div>}
              {result.missed_words?.length > 0 && <div className="text-xs text-white/50 mt-1">À travailler : {result.missed_words.join(', ')}</div>}
            </>
          )}
          <button onClick={reset} className="mt-3 text-xs bg-white/10 hover:bg-white/20 px-4 py-1.5 rounded-full" data-testid="pron-retry">Réessayer</button>
        </div>
      )}
    </div>
  );
}

function Step4Audio({ items, direction = 'fr2en', onDone, lang = 'fr', token }) {
  const t = getT(lang);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [rate, setRate] = useState(0.85);
  const [autoplay, setAutoplay] = useState(true);
  const timerRef = useRef();
  const playAndPauseRef = useRef();
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const raw = items[idx];
  // direction === 'fr2en' (default) : we teach English → big = text (EN), small = translation (FR), TTS = en-US
  // direction === 'en2fr' : we teach French → big = translation (FR), small = text (EN), TTS = fr-FR
  const it = raw ? (direction === 'en2fr'
    ? { text: raw.translation, translation: raw.text }
    : raw) : null;
  const ttsLang = direction === 'en2fr' ? 'fr-FR' : 'en-US';

  const playAndPause = async () => {
    if (!it) return;
    setPhase('listening');
    await speak(it.text, ttsLang, rate);
    setPhase('repeat');
    const pauseMs = Math.max(2500, it.text.length * 80);
    timerRef.current = setTimeout(() => {
      setPhase('idle');
      if (idx < items.length - 1) {
        setIdx(idx + 1);
        if (autoplay) timerRef.current = setTimeout(() => playAndPauseRef.current?.(), 400);
      } else onDone();
    }, pauseMs);
  };
  playAndPauseRef.current = playAndPause;

  if (!it) return null;
  const replay = () => speak(it.text, ttsLang, rate);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs text-emerald-300 uppercase tracking-wider"><Volume2 size={14} />{t('lesson_step4_label')}</div>
        <div className="text-xs text-white/50">{idx + 1} / {items.length}</div>
      </div>
      <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 rounded-2xl p-8 text-center">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${phase === 'listening' ? 'bg-emerald-500 animate-pulse' : phase === 'repeat' ? 'bg-yellow-500 animate-pulse' : 'bg-white/10'}`}>
          {phase === 'listening' ? <Volume2 size={32} className="text-white" /> : phase === 'repeat' ? <Mic size={32} className="text-white" /> : <Play size={32} className="text-white" />}
        </div>
        <div className="text-2xl font-bold mb-2">{it.text}</div>
        <div className="text-sm text-white/60 mb-6">{it.translation}</div>
        {phase === 'listening' && <div className="text-xs text-emerald-300">{t('lesson_listen_attentively')}</div>}
        {phase === 'repeat'    && <div className="text-xs text-yellow-300">{t('lesson_your_turn')}</div>}
        {phase === 'idle' && (
          <div className="flex flex-wrap justify-center gap-2">
            <button onClick={playAndPause} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 px-6 rounded-xl flex items-center gap-2" data-testid="audio-play">
              <Play size={16} />{t('lesson_listen')}
            </button>
            <button onClick={replay} className="bg-white/10 hover:bg-white/20 py-3 px-4 rounded-xl flex items-center gap-1 text-sm" data-testid="audio-replay">
              <Volume2 size={14} />{t('lesson_replay')}
            </button>
            <button onClick={() => { if (idx < items.length - 1) setIdx(idx + 1); else onDone(); }} className="bg-white/10 hover:bg-white/20 py-3 px-4 rounded-xl flex items-center gap-1 text-sm" data-testid="audio-skip">
              {t('lesson_skip')}<ChevronRight size={14} />
            </button>
          </div>
        )}
        {token && <PronunciationCheck target={it.text} lang={ttsLang} token={token} />}
        <div className="flex items-center justify-center gap-4 mt-5 text-xs">
          <label className="flex items-center gap-1.5 text-white/60">
            <Gauge size={12} />{t('lesson_speed')}
            <select value={rate} onChange={e => setRate(Number(e.target.value))} className="bg-slate-800 rounded px-2 py-1" data-testid="audio-rate">
              <option value="0.7">{t('lesson_speed_slow')}</option>
              <option value="0.85">{t('lesson_speed_normal')}</option>
              <option value="1.0">{t('lesson_speed_fast')}</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-white/60 cursor-pointer">
            <input type="checkbox" checked={autoplay} onChange={e => setAutoplay(e.target.checked)} data-testid="audio-autoplay" />
            {t('lesson_continuous')}
          </label>
        </div>
      </div>
    </div>
  );
}

// === FAVORITES (inline) ===
function Favorites({ token, onBack, onOpenLesson, lang = 'fr' }) {
  const t = getT(lang);
  const [items, setItems] = useState([]);
  useEffect(() => { apiX('/favorites', 'GET', null, token).then(setItems).catch(()=>{}); }, [token]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-8" data-testid="favorites-page">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />{t('back')}</button>
      <div className="flex items-center gap-2 mb-2"><Star size={20} className="text-yellow-400 fill-yellow-400" /><h2 className="text-2xl font-bold">{t('fav_title')}</h2></div>
      <p className="text-white/60 text-sm mb-6">{t('fav_sub')}</p>
      {items.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center text-white/60 text-sm">{t('fav_empty')}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map(l => (
            <button key={l.id} onClick={() => onOpenLesson(l)} data-testid={`fav-item-${l.id}`}
              className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition">
              <div className="flex items-center justify-between"><span className="text-xs text-yellow-300 font-bold">{l.level}</span><Star size={12} className="text-yellow-400 fill-yellow-400" /></div>
              <div className="font-bold mt-2">{l.title}</div>
              <div className="text-xs text-white/50 mt-2">{l.module_id}</div>
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// === PROFILE ===
function Profile({ user, token, lang = 'fr' }) {
  const t = getT(lang);
  const [progress, setProgress] = useState([]);
  useEffect(() => { api('/progress', 'GET', null, token).then(setProgress).catch(()=>{}); }, [token]);
  if (!user) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-10">
      <div className="bg-gradient-to-br from-emerald-700/30 to-slate-900 rounded-2xl p-8 border border-emerald-500/20">
        <div className="text-3xl mb-2">👋</div>
        <h2 className="text-2xl font-bold">{user.name}</h2>
        <p className="text-white/60 text-sm">{user.email}</p>
        <div className="grid grid-cols-3 gap-3 mt-6">
          <Stat icon={Trophy} label={t('profile_xp')} value={user.xp} color="text-yellow-400" />
          <Stat icon={Flame}  label={t('profile_streak')} value={`${user.streak_days || 0}${lang==='fr'?'j':'d'}`} color="text-orange-400" />
          <Stat icon={Heart}  label={t('profile_lives')} value={user.lives} color="text-red-400" />
        </div>
      </div>
      <div className="mt-6 bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="font-bold mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-emerald-400" />{t('profile_my_progress')}</h3>
        <div className="text-sm text-white/60">{progress.length} {t('profile_steps_done')}</div>
      </div>
    </motion.div>
  );
}

function Stat({ icon:Icon, label, value, color }) {
  return (
    <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-center">
      <Icon size={18} className={`mx-auto mb-1 ${color}`} />
      <div className="font-bold text-lg">{value}</div>
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
    </div>
  );
}

// === AUTH ===
function Auth({ onAuth, lang = 'fr' }) {
  const t = getT(lang);
  const [mode, setMode] = useState('login');
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [pwd, setPwd] = useState('');
  const [phone, setPhone] = useState(''); const [city, setCity] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api('/countries').then(setCountries).catch(() => {}); }, []);

  // Auto-détection du pays via l'indicatif téléphonique
  const onPhoneChange = (val) => {
    setPhone(val);
    const digits = val.replace(/[^0-9]/g, '');
    if (digits.length >= 1 && countries.length) {
      const matches = countries
        .filter(c => digits.startsWith(c.dial))
        .sort((a, b) => b.dial.length - a.dial.length);
      if (matches[0] && !countryCode) setCountryCode(matches[0].code);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      if (!countryCode) return toast.error(lang === 'fr' ? 'Choisissez votre pays' : 'Please select your country');
      if (!phone.trim()) return toast.error(lang === 'fr' ? 'Numéro de téléphone requis' : 'Phone number required');
      if (!city.trim()) return toast.error(lang === 'fr' ? 'Ville requise' : 'City required');
    }
    setLoading(true);
    try {
      const country = countries.find(c => c.code === countryCode);
      const r = mode === 'login'
        ? await api('/auth/login', 'POST', { email, password: pwd })
        : await api('/auth/register', 'POST', {
            name, email, password: pwd,
            phone: phone.trim(), country_code: countryCode,
            country_name: country?.name || '', city: city.trim(),
          });
      onAuth(r.token, r.user, r.admin_token || null);
      setPwd('');
      if (!r.admin_token) toast.success(mode === 'login' ? t('auth_login_success') : t('auth_register_success'));
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const selCountry = countries.find(c => c.code === countryCode);
  const inputCls = "w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-sm";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md mx-auto px-4 py-12">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex border-b border-white/10 mb-6">
          <button onClick={() => setMode('login')}    className={`flex-1 py-2 text-sm font-bold ${mode==='login' ?'text-emerald-300 border-b-2 border-emerald-300':'text-white/40'}`} data-testid="academy-tab-login"><LogIn size={14} className="inline mr-1" />{t('auth_login_tab')}</button>
          <button onClick={() => setMode('register')} className={`flex-1 py-2 text-sm font-bold ${mode==='register'?'text-emerald-300 border-b-2 border-emerald-300':'text-white/40'}`} data-testid="academy-tab-register"><UserPlus size={14} className="inline mr-1" />{t('auth_register_tab')}</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === 'register' && (
            <input value={name} onChange={e=>setName(e.target.value)} placeholder={t('auth_your_name')} required className={inputCls} data-testid="academy-name-input" />
          )}
          <input value={email} onChange={e=>setEmail(e.target.value)} type="email" autoComplete="username" placeholder={t('auth_email')} required className={inputCls} data-testid="academy-email-input" />
          <input value={pwd} onChange={e=>setPwd(e.target.value)} type="password" autoComplete={mode==='login'?'current-password':'new-password'} placeholder={t('auth_password')} required className={inputCls} data-testid="academy-pwd-input" />

          {mode === 'register' && (
            <>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input value={phone} onChange={e=>onPhoneChange(e.target.value)} type="tel"
                  placeholder={selCountry ? `+${selCountry.dial} ...` : (lang==='fr' ? 'Numéro de téléphone (ex: +243...)' : 'Phone number')}
                  required className={inputCls + ' pl-9'} data-testid="academy-phone-input" />
              </div>

              <div className="relative">
                <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 z-10" />
                <select value={countryCode} onChange={e=>setCountryCode(e.target.value)} required
                  className={inputCls + ' pl-9 appearance-none'} data-testid="academy-country-select">
                  <option value="">{lang==='fr' ? 'Sélectionnez votre pays…' : 'Select your country…'}</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.name} (+{c.dial})</option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input value={city} onChange={e=>setCity(e.target.value)} placeholder={lang==='fr' ? 'Ville' : 'City'}
                  required className={inputCls + ' pl-9'} data-testid="academy-city-input" />
              </div>
            </>
          )}

          <button disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-lg flex items-center justify-center gap-2" data-testid="academy-submit-auth">
            {loading ? <Loader2 size={16} className="animate-spin" /> : (mode === 'login' ? t('auth_login_btn') : t('auth_create_mine'))}
          </button>
        </form>
      </div>
    </motion.div>
  );
}

// === ADMIN PANEL ===
function AdminPanel({ token, setToken, onRelogin, modules, reload, onPreviewSite, lang = 'fr' }) {
  const t = getT(lang);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('generate');
  const [adminLessons, setAdminLessons] = useState([]);
  const [contactUserId, setContactUserId] = useState(null);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!token) return;
    api('/admin/messages/unread-count', 'GET', null, token).then(d => setMsgUnread(d.unread || 0)).catch(() => {});
  }, [token, tab]);

  const openContact = (uid) => { setContactUserId(uid); setTab('messages'); };

  const downloadProgram = async () => {
    try {
      const r = await fetch(`${API}/admin/course-document`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'JoSmartAcademy-Programme-Anglais.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(lang === 'fr' ? 'Téléchargement impossible' : 'Download failed'); }
  };

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      setStats(await api('/admin/stats', 'GET', null, token));
      setAdminLessons(await api('/admin/lessons', 'GET', null, token));
    } catch (e) { setToken(''); }
  }, [token, setToken]);

  useEffect(() => { loadStats(); }, [loadStats]);

  if (!token) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-testid="admin-relogin">
          <div className="flex items-center justify-center gap-2 mb-3"><Lock size={18} className="text-emerald-400" /><h3 className="font-bold">{lang === 'fr' ? 'Accès administrateur' : 'Admin access'}</h3></div>
          <p className="text-sm text-white/70 mb-4">
            {lang === 'fr'
              ? "Connecte-toi avec ton compte administrateur depuis la page de connexion habituelle (ton email + ton mot de passe)."
              : 'Log in with your admin account from the usual login page (your email + password).'}
          </p>
          <button onClick={onRelogin} className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-2.5 rounded-lg" data-testid="admin-goto-login">
            {lang === 'fr' ? 'Aller à la connexion' : 'Go to login'}
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto px-4 py-10">
      {/* Bandeau admin + bouton voir le site */}
      <div className="bg-yellow-500/15 border border-yellow-500/30 rounded-xl p-3 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm">
          <ShieldCheck size={18} className="text-yellow-300" />
          <span className="font-bold text-yellow-200">{t('admin_mode_active')}</span>
          <span className="text-white/60 hidden sm:inline">{t('admin_mode_hint')}</span>
        </div>
        <button onClick={onPreviewSite} data-testid="admin-preview-site"
          className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs">
          <Eye size={14} />{t('admin_preview_site')}
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Settings size={22} className="text-emerald-400" />{t('admin_panel_title')}</h2>
        <div className="flex items-center gap-3">
          <button onClick={downloadProgram} data-testid="admin-download-program" className="text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 px-3 py-1.5 rounded-lg font-semibold">
            📄 Programme (PDF)
          </button>
          <button onClick={() => setToken('')} className="text-xs text-white/50 hover:text-white"><LogOut size={14} className="inline" /> {t('admin_logout')}</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatBlock label={t('admin_stat_users')} value={stats.users} icon={Users} />
          <StatBlock label={t('admin_stat_premium')} value={stats.premium_users} icon={Award} />
          <StatBlock label={t('admin_stat_modules')} value={stats.modules} icon={BookOpen} />
          <StatBlock label={t('admin_stat_lessons')} value={stats.lessons} icon={GraduationCap} />
          <StatBlock label={t('admin_stat_steps')} value={stats.completed_steps} icon={CheckCircle} />
        </div>
      )}

      <div className="flex gap-2 border-b border-white/10 mb-5 text-sm overflow-x-auto whitespace-nowrap">
        {[['generate',t('admin_tab_generate')],['analytics','📊 Visites'],['messages',<span key="m" className="inline-flex items-center gap-1">💬 Messages{msgUnread>0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">{msgUnread}</span>}</span>],['drafts','Brouillons'],['lessons',t('admin_tab_lessons')],['users',t('admin_tab_users')],['pricing',t('admin_tab_pricing')],['countries','Prix par pays'],['grants','Grâces'],['payments',t('admin_tab_payments')],['revenue',t('admin_tab_revenue')],['paysettings',t('admin_tab_paysettings')],['settings',t('admin_tab_settings')],['supporters','Soutiens'],['enrollments','Inscriptions']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} className={`px-3 py-2 ${tab===k?'border-b-2 border-emerald-400 text-emerald-300':'text-white/60 hover:text-white'}`} data-testid={`academy-admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab==='generate'   && <GenerateTab token={token} modules={modules} reload={() => { reload(); loadStats(); }} lang={lang} />}
      {tab==='analytics'  && <AnalyticsTab token={token} />}
      {tab==='messages'   && <AdminMessages token={token} initialUserId={contactUserId} onUnreadChange={setMsgUnread} />}
      {tab==='drafts'     && <AdminDrafts token={token} modules={modules} reload={() => { reload(); loadStats(); }} />}
      {tab==='lessons'    && <LessonsTab lessons={adminLessons} token={token} reload={loadStats} lang={lang} />}
      {tab==='users'      && <UsersTab token={token} lang={lang} onContact={openContact} />}
      {tab==='pricing'    && <AdminPricing token={token} />}
      {tab==='countries'  && <AdminCountryPricing token={token} />}
      {tab==='grants'     && <AdminGrants token={token} modules={modules} />}
      {tab==='payments'   && <AdminPayments token={token} />}
      {tab==='revenue'    && <AdminRevenue token={token} />}
      {tab==='paysettings'&& <AdminPaymentSettings token={token} />}
      {tab==='settings'   && <SettingsTab token={token} lang={lang} />}
      {tab==='supporters' && <SupportersTab token={token} />}
      {tab==='enrollments' && <EnrollmentsTab token={token} />}
    </motion.div>
  );
}

function AnalyticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const reload = useCallback(() => {
    setLoading(true); setErr(false);
    api('/admin/analytics', 'GET', null, token)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setErr(true); setLoading(false); });
  }, [token]);
  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="text-white/60" data-testid="analytics-loading">Chargement des statistiques…</div>;
  if (err || !data) return <div className="text-white/60">Impossible de charger les statistiques.</div>;

  const maxTrend = Math.max(1, ...data.daily.map(d => d.visits));
  const maxView = Math.max(1, ...data.top_views.map(v => v.count));

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/50">Inclut tous les visiteurs, même ceux sans compte.</p>
        <button onClick={reload} className="text-xs bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg" data-testid="analytics-refresh">↻ Actualiser</button>
      </div>

      {/* Cartes chiffres clés */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBlock label="Visites totales" value={data.total_visits} icon={Eye} />
        <StatBlock label="Visiteurs uniques" value={data.unique_visitors} icon={Users} />
        <StatBlock label="Visiteurs sans compte" value={data.guest_visitors} icon={Users} />
        <StatBlock label="Visites aujourd'hui" value={data.visits_today} icon={CheckCircle} />
        <StatBlock label="Visites (7 jours)" value={data.visits_7d} icon={Award} />
      </div>

      {/* Tendance des visites */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="analytics-trend">
        <h3 className="font-semibold text-sm mb-4 text-white/80">Visites par jour (14 derniers jours)</h3>
        {data.daily.length === 0 ? (
          <div className="text-white/40 text-sm py-6 text-center">Pas encore de données à afficher.</div>
        ) : (
          <div className="flex items-end gap-3 h-36 border-b border-white/10 overflow-x-auto pb-0">
            {data.daily.map((d, i) => (
              <div key={i} className="flex flex-col items-center justify-end gap-1.5 shrink-0" style={{ width: 26 }} title={`${d.date} : ${d.visits} visite(s)`}>
                <span className="text-[11px] text-white/70 font-medium">{d.visits}</span>
                <div className="w-full rounded-t-sm bg-emerald-600/60"
                     style={{ height: `${Math.max(3, (d.visits / maxTrend) * 96)}px` }} />
                <span className="text-[9px] text-white/35">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
        {data.daily.length > 0 && (
          <p className="text-[11px] text-white/35 mt-3">Chaque barre = le nombre de visites de ce jour. Les données s'étofferont au fil du temps.</p>
        )}
      </div>

      {/* Modules les plus cliqués */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="analytics-top-views">
        <h3 className="font-semibold text-sm mb-1 text-white/80">Pages / modules les plus consultés</h3>
        <p className="text-[11px] text-white/40 mb-4">Classé par nombre de clics. « Uniques » = nombre de visiteurs différents.</p>
        {data.top_views.length === 0 ? (
          <div className="text-white/40 text-sm py-4">Pas encore de clics enregistrés.</div>
        ) : (
          <div className="space-y-2.5">
            {data.top_views.map((v, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`analytics-view-row-${i}`}>
                <div className="w-36 sm:w-48 text-sm text-white/80 truncate">{v.label}</div>
                <div className="flex-1 bg-white/5 rounded h-2 overflow-hidden">
                  <div className="bg-emerald-600/50 h-full rounded"
                       style={{ width: `${Math.max(4, (v.count / maxView) * 100)}%` }} />
                </div>
                <div className="w-24 text-right text-xs text-white/55 tabular-nums">
                  <span className="text-white/80 font-medium">{v.count}</span> · {v.unique} uniq.
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SupportersTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api('/admin/supporters', 'GET', null, token).then(d => { setRows(d); setLoading(false); }).catch(() => setLoading(false)); }, [token]);
  if (loading) return <div className="text-white/60">Chargement...</div>;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="supporters-tab">
      <h3 className="font-bold mb-1">Personnes qui ont soutenu ({rows.length})</h3>
      <p className="text-xs text-white/50 mb-4">Soumissions du formulaire « Soutenir ». Elles indiquent qui compte envoyer un montant par e-Transfer.</p>
      {rows.length === 0 ? <div className="text-white/50 text-sm">Aucun soutien pour l’instant.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-white/50 text-left border-b border-white/10">
              <th className="py-2 pr-3">Nom</th><th className="py-2 pr-3">Montant</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Téléphone</th><th className="py-2 pr-3">Message</th><th className="py-2">Date</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/5" data-testid={`supporter-row-${i}`}>
                  <td className="py-2 pr-3 font-semibold text-white">{r.name}</td>
                  <td className="py-2 pr-3 text-emerald-300">{r.amount || '—'}</td>
                  <td className="py-2 pr-3 text-white/70">{r.email || '—'}</td>
                  <td className="py-2 pr-3 text-white/70">{r.phone || '—'}</td>
                  <td className="py-2 pr-3 text-white/60 max-w-[200px] truncate">{r.message || '—'}</td>
                  <td className="py-2 text-white/40 text-xs">{(r.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EnrollmentsTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api('/admin/enrollments', 'GET', null, token).then(d => { setRows(d); setLoading(false); }).catch(() => setLoading(false)); }, [token]);
  if (loading) return <div className="text-white/60">Chargement...</div>;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="enrollments-tab">
      <h3 className="font-bold mb-1">Inscriptions aux formations ({rows.length})</h3>
      <p className="text-xs text-white/50 mb-4">Soumissions du formulaire d'inscription des Formations Pro (présentiel / en ligne).</p>
      {rows.length === 0 ? <div className="text-white/50 text-sm">Aucune inscription pour l'instant.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-white/50 text-left border-b border-white/10">
              <th className="py-2 pr-3">Nom</th><th className="py-2 pr-3">Formation</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Téléphone</th><th className="py-2 pr-3">Message</th><th className="py-2">Date</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/5" data-testid={`enrollment-row-${i}`}>
                  <td className="py-2 pr-3 font-semibold text-white">{r.name}</td>
                  <td className="py-2 pr-3 text-blue-300">{r.formation || '—'}</td>
                  <td className="py-2 pr-3 text-white/70">{r.email || '—'}</td>
                  <td className="py-2 pr-3 text-white/70">{r.phone || '—'}</td>
                  <td className="py-2 pr-3 text-white/60 max-w-[200px] truncate">{r.message || '—'}</td>
                  <td className="py-2 text-white/40 text-xs">{(r.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function StatBlock({ label, value, icon:Icon }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3">
      <Icon size={14} className="text-emerald-400 mb-1" />
      <div className="font-bold text-xl">{value}</div>
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function GenerateTab({ token, modules, reload, lang = 'fr' }) {
  const t = getT(lang);
  const [title, setTitle] = useState(''); const [level, setLevel] = useState('A1');
  const [moduleId, setModuleId] = useState(modules[0]?.id || 'english-a1');
  const [order, setOrder] = useState(0);
  const [generated, setGenerated] = useState(null);
  const [loading, setLoading] = useState(false);

  const gen = async () => {
    if (!title.trim()) return;
    setLoading(true); setGenerated(null);
    try {
      const r = await api('/admin/generate-lesson', 'POST', { title, level, source_lang: 'fr', target_lang: 'en' }, token);
      setGenerated(r);
      toast.success(t('admin_gen_success'));
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  const publish = async () => {
    try {
      await api('/admin/lessons', 'POST', { module_id: moduleId, title: generated.title, level: generated.level, content: generated.content, order: Number(order) || 0 }, token);
      toast.success(t('admin_gen_published')); setGenerated(null); setTitle(''); reload();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <input value={title} onChange={e=>setTitle(e.target.value)} placeholder={t('admin_gen_title_ph')} className="bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm" data-testid="gen-title" />
        <select value={level} onChange={e=>setLevel(e.target.value)} className="bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm" data-testid="gen-level">
          {['A1','A2','B1','B2','C1'].map(l=><option key={l} value={l}>{l}</option>)}
        </select>
        <select value={moduleId} onChange={e=>setModuleId(e.target.value)} className="bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm" data-testid="gen-module">
          {modules.map(m=><option key={m.id} value={m.id}>{m.title_fr}</option>)}
        </select>
      </div>
      <button onClick={gen} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-2.5 px-5 rounded-lg flex items-center gap-2" data-testid="gen-btn">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {t('admin_gen_btn')}
      </button>

      {generated && (
        <div className="bg-slate-900/60 rounded-xl p-4 border border-emerald-500/30 space-y-3">
          <div className="text-xs text-emerald-300">{t('admin_gen_preview')} — {generated.content.step1_lecture?.items?.length || 0} {lang==='fr'?'mots':'words'}, {generated.content.step2_exercises?.length || 0} {lang==='fr'?'exos':'ex'}, {generated.content.step3_minichat?.length || 0} chats, {generated.content.step4_audio?.length || 0} audios</div>
          <pre className="text-[11px] text-white/70 max-h-60 overflow-y-auto">{JSON.stringify(generated.content, null, 2)}</pre>
          <div className="flex gap-2 items-center">
            <input type="number" value={order} onChange={e=>setOrder(e.target.value)} placeholder={t('admin_gen_order')} className="bg-slate-800 border border-white/10 rounded p-2 text-sm w-24" />
            <button onClick={publish} className="bg-yellow-500 hover:bg-yellow-600 text-slate-950 font-bold py-2 px-4 rounded-lg flex items-center gap-1 text-sm" data-testid="publish-lesson"><Save size={14} />{t('admin_gen_publish')}</button>
            <button onClick={()=>setGenerated(null)} className="text-white/50 text-sm">{t('admin_gen_cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LessonsTab({ lessons, token, reload, lang = 'fr' }) {
  const t = getT(lang);
  const del = async (id) => {
    if (!window.confirm(t('admin_del_confirm'))) return;
    await api(`/admin/lessons/${id}`, 'DELETE', null, token);
    toast.success(t('admin_del_ok')); reload();
  };
  return (
    <div className="space-y-2">
      {lessons.map(l => (
        <div key={l.id} className="bg-white/5 border border-white/10 rounded-lg p-3 flex justify-between items-center">
          <div>
            <div className="font-bold text-sm">{l.title}</div>
            <div className="text-xs text-white/50">{l.level} · {lang==='fr'?'ordre':'order'} {l.order} · {l.module_id}</div>
          </div>
          <button onClick={() => del(l.id)} className="text-red-300 hover:text-red-200" data-testid={`del-lesson-${l.id}`}><Trash2 size={14} /></button>
        </div>
      ))}
      {lessons.length === 0 && <div className="text-center text-white/40 py-8 text-sm">{t('admin_no_lessons')}</div>}
    </div>
  );
}

function UsersTab({ token, lang = 'fr', onContact }) {
  const t = getT(lang);
  const [users, setUsers] = useState([]);
  useEffect(() => { api('/admin/users', 'GET', null, token).then(setUsers).catch(()=>{}); }, [token]);
  return (
    <div>
      <AdminCountryStats token={token} />
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/5 text-white/60 text-xs"><tr><th className="text-left p-2">{t('admin_col_name')}</th><th className="text-left p-2">{t('admin_col_email')}</th><th className="text-left p-2">Pays</th><th className="text-left p-2">Ville</th><th className="text-left p-2">Téléphone</th><th className="text-left p-2">{t('admin_col_xp')}</th><th className="text-left p-2"></th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-white/5"><td className="p-2">{u.name}</td><td className="p-2 text-white/70">{u.email}</td><td className="p-2 text-white/70">{u.country_name || u.country_code || '—'}</td><td className="p-2 text-white/70">{u.city || '—'}</td><td className="p-2 text-white/70">{u.phone || '—'}</td><td className="p-2">{u.xp}</td><td className="p-2">{!u.is_admin && <button onClick={() => onContact && onContact(u.id)} data-testid={`contact-user-${u.id}`} className="text-xs bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-200 px-2.5 py-1 rounded-lg whitespace-nowrap">✉️ Contacter</button>}</td></tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="p-6 text-center text-white/40 text-sm">{t('admin_no_users')}</div>}
      </div>
    </div>
  );
}

function SettingsTab({ token, lang = 'fr' }) {
  const t = getT(lang);
  const [cfg, setCfg] = useState(null);
  const [openAccess, setOpenAccess] = useState(false);
  const [savingOpen, setSavingOpen] = useState(false);
  useEffect(() => { api('/config').then(c => { setCfg(c.free_access); setOpenAccess(!!c.open_access); }); }, []);
  if (!cfg) return <div>{t('dash_loading')}</div>;
  const save = async () => {
    await api('/admin/free-access', 'PUT', cfg, token);
    toast.success(t('admin_settings_saved'));
  };
  const toggleOpenAccess = async (val) => {
    setSavingOpen(true);
    try {
      await api('/admin/open-access', 'PUT', { open_access: val }, token);
      setOpenAccess(val);
      toast.success(val
        ? (lang === 'fr' ? '✅ Toutes les leçons sont maintenant OUVERTES à tous.' : '✅ All lessons are now OPEN to everyone.')
        : (lang === 'fr' ? '🔒 Accès restreint rétabli (niveaux/paiement).' : '🔒 Restricted access restored (levels/payment).'));
    } catch (e) {
      toast.error(lang === 'fr' ? 'Échec de la mise à jour.' : 'Update failed.');
    } finally { setSavingOpen(false); }
  };
  const labels = {
    step_1_lecture: t('admin_settings_step1'),
    step_2_exercises: t('admin_settings_step2'),
    step_3_minichat: t('admin_settings_step3'),
    step_4_audio: t('admin_settings_step4'),
  };
  return (
    <div className="space-y-5">
      <div className={`border rounded-2xl p-5 ${openAccess ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-white/5 border-white/10'}`} data-testid="open-access-panel">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-bold flex items-center gap-2">
              {openAccess ? <Unlock size={18} className="text-emerald-400" /> : <Lock size={18} className="text-white/50" />}
              {lang === 'fr' ? 'Ouvrir toutes les leçons (mode test)' : 'Open all lessons (test mode)'}
            </h3>
            <p className="text-sm text-white/60 mt-1 max-w-xl">
              {lang === 'fr'
                ? "Quand c'est activé, tous les niveaux et toutes les leçons sont débloqués pour tout le monde (pas de compte, pas de paiement, pas de verrouillage par niveau). Idéal pour laisser des testeurs essayer chaque leçon. Désactive-le quand tu veux pour rétablir l'accès normal."
                : 'When enabled, every level and lesson is unlocked for everyone (no account, no payment, no level locking). Turn it off anytime to restore normal access.'}
            </p>
            <div className={`text-sm font-semibold mt-2 ${openAccess ? 'text-emerald-300' : 'text-white/50'}`}>
              {openAccess ? (lang === 'fr' ? '● Statut : OUVERT à tous' : '● Status: OPEN to everyone') : (lang === 'fr' ? '● Statut : accès restreint' : '● Status: restricted access')}
            </div>
          </div>
          <button onClick={() => toggleOpenAccess(!openAccess)} disabled={savingOpen}
            data-testid="open-access-toggle"
            className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition ${openAccess ? 'bg-emerald-500' : 'bg-white/20'} ${savingOpen ? 'opacity-50' : ''}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${openAccess ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="font-bold">{t('admin_settings_free')}</h3>
        {Object.entries(labels).map(([k,l]) => (
          <label key={k} className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={cfg[k]} onChange={e=>setCfg({...cfg, [k]: e.target.checked})} data-testid={`free-${k}`} />
            <span className="text-sm">{l}</span>
          </label>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">{t('admin_settings_free_mod')}
            <input type="number" value={cfg.free_lessons_per_module} onChange={e=>setCfg({...cfg, free_lessons_per_module: Number(e.target.value)})} className="w-full bg-slate-900 border border-white/10 rounded p-2 mt-1" />
          </label>
          <label className="text-sm">{t('admin_settings_free_day')}
            <input type="number" value={cfg.free_lessons_per_day} onChange={e=>setCfg({...cfg, free_lessons_per_day: Number(e.target.value)})} className="w-full bg-slate-900 border border-white/10 rounded p-2 mt-1" />
          </label>
        </div>
        <button onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-2.5 px-5 rounded-lg" data-testid="save-settings">{t('admin_settings_save')}</button>
      </div>
    </div>
  );
}


// === ABOUT / FAQ / TERMS / CONTACT ===
function InstallPWABanner() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem('jstInstallDismissed') === '1');
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener('beforeinstallprompt', onBip);
    const onInstalled = () => { setHidden(true); localStorage.setItem('jstInstallDismissed', '1'); };
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onBip); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  const isStandalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);
  if (isStandalone || hidden) return null;

  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
    } else {
      setShowHow(true);
    }
  };
  const dismiss = () => { setHidden(true); localStorage.setItem('jstInstallDismissed', '1'); };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-3" data-testid="pwa-install-banner">
      <div className="bg-emerald-500/15 border border-emerald-400/40 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-3">
          <Download size={20} className="text-emerald-300 flex-shrink-0" />
          <div className="text-sm font-semibold text-white flex-1">📲 Installer l'application JoSmartTech</div>
          <button onClick={install} data-testid="pwa-install-btn" className="bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold text-sm px-5 py-2 rounded-full whitespace-nowrap shadow-lg shadow-emerald-500/30">Installer</button>
          <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-white/40 hover:text-white/70"><X size={18} /></button>
        </div>
        {showHow && (
          <div className="mt-2 text-xs text-white/80 border-t border-white/10 pt-2" data-testid="pwa-install-how">
            {isIOS
              ? "📱 iPhone : ouvrez le site dans Safari → bouton Partager ⬆️ → « Sur l'écran d'accueil »."
              : "📱 Android : menu ⋮ du navigateur → « Installer l'application »."}
          </div>
        )}
      </div>
    </div>
  );
}

function PageWrapper({ title, subtitle, onBack, children, lang = 'fr' }) {
  const t = getT(lang);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-4xl mx-auto px-4 py-10">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1"><ChevronLeft size={16} />{t('back')}</button>
      <h1 className="text-3xl sm:text-4xl font-black mb-2 bg-gradient-to-r from-emerald-300 to-yellow-300 bg-clip-text text-transparent">{title}</h1>
      {subtitle && <p className="text-white/60 mb-8">{subtitle}</p>}
      {children}
    </motion.div>
  );
}

function AboutPage({ onBack, lang = 'fr' }) {
  const t = getT(lang);
  return (
    <PageWrapper lang={lang} title={t('about_title')} subtitle={t('about_sub')} onBack={onBack}>
      <div className="space-y-6 text-white/80 leading-relaxed">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="font-bold text-xl mb-3 text-emerald-300">{t('about_mission')}</h2>
          <p>{t('about_mission_text')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="font-bold text-xl mb-3 text-yellow-300">{t('about_method')}</h2>
          <p>{t('about_method_intro')}</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>{t('about_method_step0')}</li>
            <li>{t('about_method_step1')}</li>
            <li>{t('about_method_step2')}</li>
            <li>{t('about_method_step3')}</li>
            <li>{t('about_method_step4')}</li>
          </ul>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="font-bold text-xl mb-3 text-emerald-300">{t('about_vision')}</h2>
          <p>{t('about_vision_text')}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/30 rounded-2xl p-6">
          <h2 className="font-bold text-xl mb-3">{t('about_brand')}</h2>
          <p>{t('about_brand_text')}</p>
        </div>
      </div>
    </PageWrapper>
  );
}

function FAQPage({ onBack, lang = 'fr' }) {
  const t = getT(lang);
  const faqs = [
    { q: t('faq_q1'),  a: t('faq_a1')  },
    { q: t('faq_q2'),  a: t('faq_a2')  },
    { q: t('faq_q3'),  a: t('faq_a3')  },
    { q: t('faq_q4'),  a: t('faq_a4')  },
    { q: t('faq_q5'),  a: t('faq_a5')  },
    { q: t('faq_q6'),  a: t('faq_a6')  },
    { q: t('faq_q7'),  a: t('faq_a7')  },
    { q: t('faq_q8'),  a: t('faq_a8')  },
    { q: t('faq_q9'),  a: t('faq_a9')  },
    { q: t('faq_q10'), a: t('faq_a10') },
  ];
  return (
    <PageWrapper lang={lang} title={t('faqp_title')} subtitle={t('faqp_sub')} onBack={onBack}>
      <div className="space-y-3">
        {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
      </div>
    </PageWrapper>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex items-center justify-between hover:bg-white/5">
        <span className="font-bold text-sm">{q}</span>
        <ChevronRight size={16} className={`text-emerald-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-white/70">{a}</div>}
    </div>
  );
}

function TermsPage({ onBack, lang = 'fr' }) {
  const t = getT(lang);
  const FR = [
    { h: '1. Acceptation des conditions', p: "En utilisant JoSmartTech Academy, vous acceptez les présentes conditions. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le service." },
    { h: '2. Création de compte',         p: "Vous devez fournir des informations exactes lors de l'inscription. Vous êtes responsable de la confidentialité de votre mot de passe." },
    { h: '3. Utilisation du service',     p: "JoSmartTech Academy est destiné à un usage personnel et éducatif. La revente, la copie ou la redistribution du contenu pédagogique sans autorisation est interdite." },
    { h: '4. Abonnement Premium',         p: "L'abonnement Premium est facturé mensuellement et peut être annulé à tout moment. Aucun remboursement n'est dû pour les périodes déjà entamées." },
    { h: '5. Propriété intellectuelle',   p: "Tout le contenu (leçons, audios, exercices, design) est la propriété de JoSmartTech. Toute reproduction sans autorisation écrite est strictement interdite." },
    { h: '6. Données personnelles',       p: "Nous collectons uniquement les données nécessaires (nom, email, progression) pour vous offrir le service. Vos données ne sont jamais revendues à des tiers." },
    { h: '7. Modification des conditions',p: "JoSmartTech se réserve le droit de modifier ces conditions à tout moment. Les modifications prennent effet dès leur publication." },
    { h: '8. Contact',                    p: "Pour toute question, contactez-nous à support.josmarttech@gmail.com." },
  ];
  const EN = [
    { h: '1. Acceptance of terms', p: "By using JoSmartTech Academy, you agree to these terms. If you do not agree, please do not use the service." },
    { h: '2. Account creation',    p: "You must provide accurate information when registering. You are responsible for keeping your password confidential." },
    { h: '3. Use of service',      p: "JoSmartTech Academy is intended for personal and educational use. Reselling, copying, or redistributing the content without authorization is prohibited." },
    { h: '4. Premium subscription',p: "The Premium subscription is billed monthly and can be cancelled at any time. No refunds are due for periods already started." },
    { h: '5. Intellectual property', p: "All content (lessons, audio, exercises, design) is the property of JoSmartTech. Any reproduction without written authorization is strictly forbidden." },
    { h: '6. Personal data',       p: "We only collect the data necessary (name, email, progress) to provide the service. Your data is never sold to third parties." },
    { h: '7. Changes to terms',    p: "JoSmartTech reserves the right to amend these terms at any time. Changes take effect upon publication." },
    { h: '8. Contact',             p: "For any question, contact us at support.josmarttech@gmail.com." },
  ];
  const items = lang === 'en' ? EN : FR;
  const lastUpdated = lang === 'en' ? 'Last updated: February 2026' : 'Dernière mise à jour : Février 2026';
  return (
    <PageWrapper lang={lang} title={t('terms_title')} subtitle={lastUpdated} onBack={onBack}>
      <div className="prose prose-invert space-y-4 text-sm text-white/70 leading-relaxed">
        {items.map((it, i) => (
          <section key={i}>
            <h3 className="text-emerald-300 font-bold text-base">{it.h}</h3>
            <p>{it.p}</p>
          </section>
        ))}
      </div>
    </PageWrapper>
  );
}

function ContactPage({ onBack, lang = 'fr' }) {
  const t = getT(lang);
  const subtitle = lang === 'en' ? 'Our team usually replies within 24h' : 'Notre équipe répond généralement sous 24h';
  const mainSite = lang === 'en' ? 'Main website' : 'Site principal';
  const hoursTitle = lang === 'en' ? 'Support hours' : 'Horaires de support';
  const monFri = lang === 'en' ? 'Monday — Friday: 9 AM — 6 PM (Winnipeg time)' : 'Lundi — Vendredi : 9h — 18h (heure de Winnipeg)';
  const sat = lang === 'en' ? 'Saturday: 10 AM — 2 PM' : 'Samedi : 10h — 14h';
  const urgent = lang === 'en' ? 'For technical emergencies, prefer WhatsApp.' : 'Pour les urgences techniques, privilégiez WhatsApp.';
  return (
    <PageWrapper lang={lang} title={t('contact_title')} subtitle={subtitle} onBack={onBack}>
      <div className="grid sm:grid-cols-3 gap-4">
        <a href="mailto:support.josmarttech@gmail.com" className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl p-6 hover:scale-[1.02] transition" data-testid="contact-email">
          <div className="text-3xl mb-3">📧</div>
          <div className="font-bold">Email</div>
          <div className="text-xs text-white/80 break-all">support.josmarttech@gmail.com</div>
        </a>
        <a href="https://wa.me/14313345083" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-2xl p-6 hover:scale-[1.02] transition" data-testid="contact-whatsapp">
          <div className="text-3xl mb-3">📱</div>
          <div className="font-bold">WhatsApp</div>
          <div className="text-xs text-white/80">+1 431-334-5083</div>
        </a>
        <a href="/" className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-6 hover:scale-[1.02] transition" data-testid="contact-web">
          <div className="text-3xl mb-3">🌐</div>
          <div className="font-bold">{mainSite}</div>
          <div className="text-xs text-white/80">josmarttech.net</div>
        </a>
      </div>
      <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-6 text-sm text-white/70">
        <p className="mb-3"><strong>{hoursTitle}</strong></p>
        <p>{monFri}</p>
        <p>{sat}</p>
        <p className="mt-3 text-xs text-white/50">{urgent}</p>
      </div>
    </PageWrapper>
  );
}

